import Phaser from 'phaser';
import {
  addPowerCharge,
  BattleState,
  computeStars,
  consumeAttackSkip,
  consumeConfusedAttack,
  consumeFirstAttackFree,
  consumePower,
  damageEnemy,
  damageForMatch,
  damageHero,
  freezeEnemyAttack,
  HERO_MAX_HP,
  initialBattle,
  markStoneUsed,
  POWER_MAX_CHARGES,
  powerReady,
  queueAttackSkip,
  tickTimer,
} from './battle';
import { InfinityStoneId, STONES } from './stones';
import {
  applyGravity,
  areAdjacent,
  createBoard,
  inBounds,
  refill,
  swapCells,
} from './board';
import {
  createAnimFromFrameSet,
  frameKey,
  preloadFrameSet,
} from './assets';
import { ENEMIES, EnemySpec } from './enemies';
import { drawClaws } from './icons/clawsIcon';
import { drawEye } from './icons/eyeIcon';
import { drawFist } from './icons/fistIcon';
import { drawHammer } from './icons/hammerIcon';
import { drawAtom } from './icons/atomIcon';
import { drawBolt } from './icons/boltIcon';
import { drawAdamantium } from './icons/adamantiumIcon';
import { drawBlaster } from './icons/blasterIcon';
import { drawBillyclub } from './icons/billyclubIcon';
import { drawFlame } from './icons/flameIcon';
import { drawPowerSword } from './icons/powerSwordIcon';
import { drawDragonDagger } from './icons/dragonDaggerIcon';
import { drawPowerBow } from './icons/powerBowIcon';
import { drawGem } from './icons/gemIcon';
import { drawGhostWeb } from './icons/ghostWebIcon';
import { drawHex } from './icons/hexIcon';
import { drawPhoton } from './icons/photonIcon';
import { applyMuteFromSave, playSFX } from './audio';
import { drawRepulsor } from './icons/repulsorIcon';
import { drawShield } from './icons/shieldIcon';
import { drawWeb } from './icons/webIcon';
import { POWERS } from './powers';
import { HeroId, HEROES } from './heroes';
import { LevelConfig, levelById, WORLDS } from './levels';
import {
  clearFailures,
  failuresFor,
  loadSave,
  markTutorialSeen,
  powerUpHpScale,
  recordFailure,
  resolveEquippedStone,
  recordStars,
  setMuted,
  unlockHero,
  vibraniumBuffs,
} from './save';
import { pairKey, resolveTeamUp, TeamUpSpec } from './teamups';
import { findMatches } from './matching';
import { findFirstHint } from './hints';
import { Cell, COLS, Grid, ROWS, Tile, TileId, TileKind } from './types';

type PowerVisual = {
  label: string;
  color: number;
  flash: [number, number, number];
  fromSky?: boolean;
};

const POWER_VISUALS: Partial<Record<HeroId, PowerVisual>> = {
  'captain-america': { label: 'SHIELD THROW!', color: 0x3b82f6, flash: [60, 130, 246] },
  'black-panther': { label: 'CLAW STRIKE!', color: 0xa855f7, flash: [150, 80, 220] },
  'iron-man': { label: 'REPULSOR!', color: 0xfbbf24, flash: [255, 210, 80] },
  'spider-man': { label: 'WEB STRIKE!', color: 0xef4444, flash: [239, 68, 68] },
  thor: { label: 'LIGHTNING!', color: 0xfde68a, flash: [253, 230, 138], fromSky: true },
  hulk: { label: 'SMASH!', color: 0x22c55e, flash: [34, 197, 94] },
  'doctor-strange': { label: 'TIME STOP!', color: 0x86efac, flash: [134, 239, 172] },
  'scarlet-witch': { label: 'REALITY WARP!', color: 0xef4444, flash: [239, 68, 68] },
  'captain-marvel': { label: 'BINARY FORM!', color: 0xfbbf24, flash: [251, 191, 36] },
  vision: { label: 'MIND STONE!', color: 0xa855f7, flash: [168, 85, 247] },
  'ant-man': { label: 'GIANT-MAN SLAM!', color: 0xef4444, flash: [239, 68, 68] },
  'ghost-spider': { label: 'DIMENSION JUMP!', color: 0xf472b6, flash: [244, 114, 182] },
  spin: { label: 'VENOM STRIKE!', color: 0x22d3ee, flash: [34, 211, 238] },
};

const TILE_FILL: Partial<Record<TileKind, number>> = {
  red: 0xef4444,
  blue: 0x3b82f6,
  green: 0x22c55e,
  yellow: 0xfbbf24,
  purple: 0xa855f7,
};

const TILE_STROKE = 0x0a0f1f;
const DRAG_THRESHOLD_FRAC = 0.3;

const DUR_SWAP = 180;
const DUR_POP = 180;
const DUR_FALL_PER_ROW = 70;
const DUR_FALL_MIN = 140;
const DUR_SHAKE = 80;

const BATTLE_BANNER_FRAC = 0.28;
const BATTLE_BANNER_MIN_H = 180;

const HP_BAR_H = 10;
const TIMER_BAR_H = 6;
const HP_COLOR_HERO = 0x22c55e;
const HP_COLOR_ENEMY = 0xef4444;
const HP_COLOR_BG = 0x1b2747;
const TIMER_COLOR = 0xfbbf24;

type DragState = {
  pointerId: number;
  startX: number;
  startY: number;
  fromCell: Cell;
  resolved: boolean;
};

export class LevelScene extends Phaser.Scene {
  private level!: LevelConfig;
  private team: HeroId[] = [];
  private allKinds: readonly TileKind[] = [];
  private heroKinds: readonly TileKind[] = [];

  private enemyAttackCount = 0;
  private telegraphActive = false;
  private telegraphTimer?: Phaser.Time.TimerEvent;
  private telegraphIcon?: Phaser.GameObjects.Graphics;
  private telegraphDimOverlay?: Phaser.GameObjects.Graphics;

  private lastInputMs = 0;
  private hintActive = false;
  private hintGlows: Phaser.GameObjects.Graphics[] = [];
  private hintTweens: Phaser.Tweens.Tween[] = [];

  private paused = false;
  private pauseModal?: Phaser.GameObjects.Container;
  private desperateMode = false;
  private teamUpMultiplier = 1.5;
  private matchBonusDamage = 0;
  private stoneStripHeight = 0;
  private stoneButtons = new Map<InfinityStoneId, Phaser.GameObjects.Container>();

  private grid!: Grid;
  private sprites = new Map<TileId, Phaser.GameObjects.Container>();
  private boardContainer!: Phaser.GameObjects.Container;
  private comboText!: Phaser.GameObjects.Text;

  private battle: BattleState = initialBattle();

  private tileSize = 96;
  private boardOriginX = 0;
  private boardOriginY = 0;
  private bannerHeight = 0;

  private heroSprites = new Map<HeroId, Phaser.GameObjects.Sprite>();
  private heroPlaceholders = new Map<HeroId, Phaser.GameObjects.Graphics>();
  private heroRings = new Map<HeroId, Phaser.GameObjects.Graphics>();
  private heroHaloTweens = new Map<HeroId, Phaser.Tweens.Tween>();
  private heroHitZones = new Map<HeroId, Phaser.GameObjects.Zone>();
  private teamUpSparks = new Map<string, Phaser.GameObjects.Container>();
  private teamUpSparkTweens = new Map<string, Phaser.Tweens.Tween>();
  private teamUpSparkStates = new Map<string, 'active' | 'ghost'>();
  private teamUpIntroShown = false;
  private enemySprite?: Phaser.GameObjects.Sprite;
  private enemyPlaceholder?: Phaser.GameObjects.Graphics;
  private heroHpBar!: Phaser.GameObjects.Graphics;
  private enemyHpBar!: Phaser.GameObjects.Graphics;
  private timerBar!: Phaser.GameObjects.Graphics;

  private modal?: Phaser.GameObjects.Container;

  private busy = false;
  private drag: DragState | null = null;

  constructor() {
    super('Level');
  }

  init(data: { levelId: string; team: HeroId[] }) {
    const level = levelById(data.levelId);
    if (!level) {
      throw new Error(`Unknown level id: ${data.levelId}`);
    }
    this.level = level;
    this.team = data.team.slice(0, 3);
    this.heroKinds = this.team.map((id) => HEROES[id].tileKind);
    this.allKinds = [...this.heroKinds, ...this.level.fillerKinds];

    this.heroSprites = new Map();
    this.heroPlaceholders = new Map();
    this.heroRings = new Map();
    this.heroHaloTweens.forEach((t) => t.stop());
    this.heroHaloTweens = new Map();
    this.heroHitZones = new Map();
    this.sprites = new Map();
    this.enemyAttackCount = 0;
    this.telegraphActive = false;
  }

  preload() {
    for (const heroId of this.team) {
      const hero = HEROES[heroId];
      preloadFrameSet(this, hero.idleFrames());
      preloadFrameSet(this, hero.attackFrames());
    }
    const enemy = ENEMIES[this.level.enemy];
    preloadFrameSet(this, enemy.idleFrames());
    preloadFrameSet(this, enemy.hitFrames());
    if (enemy.bigAttackFrames) {
      preloadFrameSet(this, enemy.bigAttackFrames());
    }
  }

  create() {
    this.cameras.main.setBackgroundColor('#0a0f1f');

    this.boardContainer = this.add.container(0, 0);

    this.comboText = this.add.text(16, 12, '', {
      fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
      fontSize: '18px',
      color: '#8ca0c4',
    });

    this.heroHpBar = this.add.graphics();
    this.enemyHpBar = this.add.graphics();
    this.timerBar = this.add.graphics();

    this.createHeroEnemyVisuals();

    this.grid = createBoard(this.allKinds);
    const save = loadSave();
    this.teamUpIntroShown = !!save.tutorialSeen['team-up-intro'];
    const buffs = vibraniumBuffs(save);
    this.teamUpMultiplier = buffs.teamUpMultiplier;
    this.matchBonusDamage = buffs.matchBonusDamage;
    const failures = failuresFor(save, this.level.id);
    const adaptive = failures >= 3;
    const desperate = failures >= 5;
    this.desperateMode = desperate;
    const heroHpMult = desperate ? 1.5 : adaptive ? 1.2 : 1;
    const progressScale = powerUpHpScale(save);
    const bossScale = this.level.isBoss ? 1.3 : 1;
    const baseEnemyHp = Math.floor(
      this.level.enemyMaxHp *
        progressScale *
        bossScale *
        (desperate ? 0.85 : 1) *
        buffs.enemyHpMult,
    );
    this.battle = initialBattle({
      enemyMaxHp: Math.max(1, baseEnemyHp),
      enemyAttackIntervalMs: this.level.enemyAttackIntervalMs,
      enemyAttackDamage: adaptive
        ? Math.max(1, this.level.enemyAttackDamage - 1)
        : this.level.enemyAttackDamage,
      heroMaxHp: Math.round(HERO_MAX_HP * heroHpMult) + buffs.heroMaxHpBonus,
      team: this.team,
      firstAttackFree: buffs.firstAttackFree,
    });
    const startCharges = (desperate ? Math.ceil(POWER_MAX_CHARGES / 2) : 0) + buffs.startCharges;
    if (startCharges > 0) {
      for (const id of this.team) {
        this.battle = addPowerCharge(this.battle, id, startCharges);
      }
    }

    this.layout(this.scale.width, this.scale.height);
    this.renderInitialGrid();
    if (this.level.portalTiles) this.ensurePortalCount(2);
    this.positionAndRedrawBattleUi();
    this.createStoneStrip();

    this.scale.on('resize', (size: Phaser.Structs.Size) => {
      this.layout(size.width, size.height);
      this.repositionAllTiles();
      this.positionAndRedrawBattleUi();
      this.repositionStoneStrip();
    });

    this.input.on('pointerdown', this.onPointerDown, this);
    this.input.on('pointermove', this.onPointerMove, this);
    this.input.on('pointerup', this.onPointerUp, this);

    this.drawGearIcon();

    this.lastInputMs = this.time.now;
    this.maybeLaunchTutorial();
  }

  private drawGearIcon() {
    const w = this.scale.width;
    const cx = w - 36;
    const cy = 28;
    const container = this.add.container(cx, cy);

    const g = this.add.graphics();
    g.fillStyle(0x121a32, 1);
    g.lineStyle(2, 0x2a3558, 1);
    g.fillCircle(0, 0, 18);
    g.strokeCircle(0, 0, 18);
    g.fillStyle(0xffd24a, 1);
    g.fillCircle(0, 0, 10);
    g.fillStyle(0x121a32, 1);
    g.fillCircle(0, 0, 5);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const x1 = Math.cos(a) * 10;
      const y1 = Math.sin(a) * 10;
      const x2 = Math.cos(a) * 15;
      const y2 = Math.sin(a) * 15;
      g.lineStyle(3, 0xffd24a, 1);
      g.beginPath();
      g.moveTo(x1, y1);
      g.lineTo(x2, y2);
      g.strokePath();
    }
    container.add(g);

    const hit = this.add.zone(0, 0, 56, 56).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => this.openPauseMenu());
    container.add(hit);
  }

  private maybeLaunchTutorial() {
    if (!this.level.tutorialKey) return;
    const save = loadSave();
    if (save.tutorialSeen[this.level.tutorialKey]) return;
    const data: Record<string, unknown> = {
      kind: this.level.tutorialKey,
      levelId: this.level.id,
    };
    if (this.level.tutorialKey === 'swap') {
      const hint = findFirstHint(this.grid);
      if (hint) {
        data.fromX = this.cellX(hint.a.col);
        data.fromY = this.cellY(hint.a.row);
        data.toX = this.cellX(hint.b.col);
        data.toY = this.cellY(hint.b.row);
      }
    }
    this.scene.launch('TutorialOverlay', data);
  }

  update(_time: number, deltaMs: number) {
    if (this.battle.phase !== 'playing') return;
    if (this.paused) return;
    const { state, attacked } = tickTimer(this.battle, deltaMs);
    this.battle = state;
    if (attacked) {
      this.onEnemyAttack();
    }
    this.drawTimerBar();
    this.maybeShowHint();
  }

  private maybeShowHint() {
    if (this.hintActive || this.busy || this.modal) return;
    if (this.time.now - this.lastInputMs < 8000) return;
    const hint = findFirstHint(this.grid);
    if (!hint) return;
    this.hintActive = true;
    for (const cell of [hint.a, hint.b]) {
      const g = this.add.graphics();
      const r = Math.max(10, this.tileSize * 0.08);
      g.lineStyle(4, 0xffd24a, 1);
      g.strokeRoundedRect(-this.tileSize / 2 + 4, -this.tileSize / 2 + 4, this.tileSize - 8, this.tileSize - 8, r);
      g.setPosition(this.cellX(cell.col), this.cellY(cell.row));
      this.boardContainer.add(g);
      this.hintGlows.push(g);
      const tween = this.tweens.add({
        targets: g,
        alpha: { from: 1, to: 0.35 },
        duration: 550,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.hintTweens.push(tween);
    }
  }

  private hideHint() {
    if (!this.hintActive) return;
    this.hintActive = false;
    for (const t of this.hintTweens) t.stop();
    this.hintTweens = [];
    for (const g of this.hintGlows) g.destroy();
    this.hintGlows = [];
  }

  private markInput() {
    this.lastInputMs = this.time.now;
    this.hideHint();
  }

  private createHeroEnemyVisuals() {
    for (const heroId of this.team) {
      const hero = HEROES[heroId];
      const idleReady = createAnimFromFrameSet(this, hero.idleAnimKey, hero.idleFrames(), {
        frameRate: 6,
        repeat: -1,
      });
      createAnimFromFrameSet(this, hero.attackAnimKey, hero.attackFrames(), {
        frameRate: 10,
        repeat: 0,
      });

      if (idleReady) {
        const firstKey = frameKey(hero.idleFrames().prefix, 0);
        const sprite = this.add.sprite(0, 0, firstKey);
        sprite.setOrigin(0.5, 1);
        sprite.play(hero.idleAnimKey);
        this.heroSprites.set(heroId, sprite);
      } else {
        this.heroPlaceholders.set(heroId, this.add.graphics());
      }

      const ring = this.add.graphics();
      this.heroRings.set(heroId, ring);

      const zone = this.add.zone(0, 0, 10, 10).setInteractive({ useHandCursor: true });
      zone.on('pointerup', () => this.onHeroTap(heroId));
      this.heroHitZones.set(heroId, zone);
    }

    const enemy = ENEMIES[this.level.enemy];
    const enemyIdleReady = createAnimFromFrameSet(this, enemy.idleAnimKey, enemy.idleFrames(), {
      frameRate: 6,
      repeat: -1,
    });
    createAnimFromFrameSet(this, enemy.hitAnimKey, enemy.hitFrames(), {
      frameRate: 10,
      repeat: 0,
    });
    if (enemy.bigAttackAnimKey && enemy.bigAttackFrames) {
      createAnimFromFrameSet(this, enemy.bigAttackAnimKey, enemy.bigAttackFrames(), {
        frameRate: 9,
        repeat: 0,
      });
    }

    if (enemyIdleReady) {
      const firstEnemyKey = frameKey(enemy.idleFrames().prefix, 0);
      this.enemySprite = this.add.sprite(0, 0, firstEnemyKey);
      this.enemySprite.setOrigin(0.5, 1);
      this.enemySprite.play(enemy.idleAnimKey);
    } else {
      this.enemyPlaceholder = this.add.graphics();
    }
  }

  private enemy(): EnemySpec {
    return ENEMIES[this.level.enemy];
  }

  private layout(w: number, h: number) {
    this.bannerHeight = Math.max(BATTLE_BANNER_MIN_H, Math.floor(h * BATTLE_BANNER_FRAC));
    const save = loadSave();
    this.stoneStripHeight = resolveEquippedStone(save) ? 44 : 0;
    const boardBottomSafe = 32;
    const boardTopPadding = 4;
    const boardAreaTop = this.bannerHeight + this.stoneStripHeight + boardTopPadding;
    const boardAreaH = Math.max(0, h - boardAreaTop - boardBottomSafe);
    const maxBoardW = Math.min(w * 0.92, boardAreaH * (COLS / ROWS));
    this.tileSize = Math.floor(maxBoardW / COLS);
    const boardW = this.tileSize * COLS;
    const boardH = this.tileSize * ROWS;
    this.boardOriginX = Math.floor((w - boardW) / 2);
    this.boardOriginY = boardAreaTop + Math.floor((boardAreaH - boardH) / 2);
  }

  private redrawHeroRing(heroId: HeroId, cx: number, cy: number, radius: number) {
    const g = this.heroRings.get(heroId);
    if (!g) return;
    const charges = this.battle.powerMeters[heroId] ?? 0;
    const frac = Math.min(1, charges / POWER_MAX_CHARGES);
    const ready = frac >= 1;
    g.clear();
    g.setPosition(cx, cy);
    g.lineStyle(5, 0x2a3558, 0.7);
    g.strokeCircle(0, 0, radius);
    if (frac > 0) {
      g.lineStyle(5, ready ? 0xffd24a : 0xfbbf24, 1);
      g.beginPath();
      g.arc(0, 0, radius, -Math.PI / 2, -Math.PI / 2 + frac * Math.PI * 2, false);
      g.strokePath();
    }
    const existingHalo = this.heroHaloTweens.get(heroId);
    if (ready) {
      if (!existingHalo) {
        playSFX('power-ready');
        g.setScale(1);
        const tween = this.tweens.add({
          targets: g,
          scale: 1.12,
          alpha: 0.85,
          duration: 500,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
        this.heroHaloTweens.set(heroId, tween);
      }
    } else {
      if (existingHalo) {
        existingHalo.stop();
        this.heroHaloTweens.delete(heroId);
        g.setScale(1);
        g.setAlpha(1);
      }
    }
  }

  private heroSpacing(): number {
    return Math.floor(this.bannerHeight * 0.52);
  }

  private heroClusterCenterX(w: number): number {
    const spacing = this.heroSpacing();
    const spriteScale = (this.bannerHeight * 0.42) / 92;
    const ringRadius = Math.max(22, 34 * spriteScale);
    const leftPad = ringRadius + 16;
    const halfSpan = ((this.team.length - 1) / 2) * spacing;
    return Math.max(Math.floor(w * 0.22), Math.floor(leftPad + halfSpan));
  }

  private heroSlotX(slotIndex: number, w: number): number {
    const spacing = this.heroSpacing();
    const offset = slotIndex - (this.team.length - 1) / 2;
    return Math.floor(this.heroClusterCenterX(w) + offset * spacing);
  }

  private positionAndRedrawBattleUi() {
    const w = this.scale.width;
    const nativeSpriteH = 92;
    const desiredSpriteH = this.bannerHeight * 0.42;
    const spriteScale = desiredSpriteH / nativeSpriteH;
    const spriteBaselineY = Math.floor(this.bannerHeight * 0.66);

    this.team.forEach((heroId, i) => {
      const hx = this.heroSlotX(i, w);
      const sprite = this.heroSprites.get(heroId);
      const placeholder = this.heroPlaceholders.get(heroId);
      if (sprite) {
        sprite.setPosition(hx, spriteBaselineY);
        sprite.setScale(spriteScale);
      } else if (placeholder) {
        const pw = 48 * spriteScale;
        const ph = 78 * spriteScale;
        placeholder.clear();
        placeholder.fillStyle(0x3b82f6, 1);
        placeholder.lineStyle(2, TILE_STROKE, 1);
        placeholder.fillRoundedRect(hx - pw / 2, spriteBaselineY - ph, pw, ph, 8);
        placeholder.strokeRoundedRect(hx - pw / 2, spriteBaselineY - ph, pw, ph, 8);
      }

      const ringRadius = Math.max(22, 34 * spriteScale);
      const ringCx = hx;
      const ringCy = spriteBaselineY - Math.max(22, 30 * spriteScale);
      this.redrawHeroRing(heroId, ringCx, ringCy, ringRadius);

      const zone = this.heroHitZones.get(heroId);
      if (zone) {
        const spacing = this.heroSpacing();
        const zoneTopY = Math.max(8, ringCy - ringRadius - 24);
        const zoneBotY = Math.floor(this.bannerHeight * 0.74);
        const zoneH = zoneBotY - zoneTopY;
        const zoneCy = (zoneTopY + zoneBotY) / 2;
        const zoneW = Math.min(spacing - 6, Math.max(ringRadius * 3.6, 130));
        zone.setPosition(ringCx, zoneCy);
        zone.setSize(zoneW, zoneH);
        zone.input!.hitArea = new Phaser.Geom.Rectangle(
          -zoneW / 2,
          -zoneH / 2,
          zoneW,
          zoneH,
        );
      }
    });

    const enemyX = Math.floor(w * 0.82);
    const enemyNativeH = this.enemy().nativeCanvasSize;
    const enemyBossBoost = this.level.isBoss ? 1.05 : 1;
    const enemyDesiredH = this.bannerHeight * 0.55 * enemyBossBoost;
    const enemyScale = enemyDesiredH / enemyNativeH;
    if (this.enemySprite) {
      this.enemySprite.setPosition(enemyX, spriteBaselineY);
      this.enemySprite.setScale(enemyScale);
    } else if (this.enemyPlaceholder) {
      const g = this.enemyPlaceholder;
      g.clear();
      const pw = 60 * spriteScale;
      const ph = 90 * spriteScale;
      g.fillStyle(0x4b5563, 1);
      g.lineStyle(2, TILE_STROKE, 1);
      g.fillRoundedRect(enemyX - pw / 2, spriteBaselineY - ph, pw, ph, 8);
      g.strokeRoundedRect(enemyX - pw / 2, spriteBaselineY - ph, pw, ph, 8);
    }

    this.drawHpBars();
    this.drawTimerBar();
    this.updateTeamUpSparks();
  }

  private drawHpBars() {
    const w = this.scale.width;
    const teamCenterX = this.heroClusterCenterX(w);
    const heroBarW = Math.min(280, Math.floor(w * 0.34));
    const heroBarX = Math.floor(teamCenterX - heroBarW / 2);
    const barY = Math.floor(this.bannerHeight * 0.76);

    this.drawHpBar(this.heroHpBar, heroBarX, barY, heroBarW, this.battle.heroHp, this.battle.heroMaxHp, HP_COLOR_HERO);

    const enemyBarW = Math.min(180, Math.floor(w * 0.24));
    const enemyX = Math.floor(w * 0.82);
    this.drawHpBar(this.enemyHpBar, enemyX - enemyBarW / 2, barY, enemyBarW, this.battle.enemyHp, this.battle.enemyMaxHp, HP_COLOR_ENEMY);
  }

  private drawHpBar(
    g: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    w: number,
    hp: number,
    maxHp: number,
    color: number,
  ) {
    g.clear();
    g.fillStyle(HP_COLOR_BG, 1);
    g.fillRoundedRect(x, y, w, HP_BAR_H, 4);
    const frac = Math.max(0, Math.min(1, hp / maxHp));
    if (frac > 0) {
      g.fillStyle(color, 1);
      g.fillRoundedRect(x, y, Math.max(4, w * frac), HP_BAR_H, 4);
    }
    g.lineStyle(1, TILE_STROKE, 1);
    g.strokeRoundedRect(x, y, w, HP_BAR_H, 4);
  }

  private drawTimerBar() {
    const w = this.scale.width;
    const barW = Math.min(220, Math.floor(w * 0.3));
    const x = Math.floor((w - barW) / 2);
    const y = Math.floor(this.bannerHeight * 0.92);
    const frac = this.battle.phase === 'playing'
      ? this.battle.enemyAttackTimerMs / this.battle.enemyAttackIntervalMs
      : 0;

    const g = this.timerBar;
    g.clear();
    g.fillStyle(HP_COLOR_BG, 1);
    g.fillRoundedRect(x, y, barW, TIMER_BAR_H, 3);
    if (frac > 0) {
      const frozen = this.battle.enemyAttackFreezeMs > 0;
      g.fillStyle(frozen ? 0x86efac : TIMER_COLOR, 1);
      g.fillRoundedRect(x, y, Math.max(2, barW * frac), TIMER_BAR_H, 3);
    }
  }

  private onEnemyAttack() {
    if (this.battle.skipNextAttacks > 0) {
      this.battle = consumeAttackSkip(this.battle);
      this.showStunnedBanner();
      return;
    }
    if (this.battle.confusedAttacks > 0) {
      this.battle = consumeConfusedAttack(this.battle);
      this.fireConfusedEnemyAttack();
      return;
    }
    if (this.battle.firstAttackFreeRemaining) {
      this.battle = consumeFirstAttackFree(this.battle);
      this.showFirstAttackBlockedBanner();
      return;
    }
    this.enemyAttackCount += 1;
    const shouldSpecial =
      !!this.level.isBoss &&
      !!this.level.bossSpecialEveryNAttacks &&
      this.enemyAttackCount > 0 &&
      this.enemyAttackCount % this.level.bossSpecialEveryNAttacks === 0;

    if (shouldSpecial) {
      this.triggerBossSpecial();
      return;
    }

    this.battle = damageHero(this.battle, this.battle.enemyAttackDamage);
    this.cameras.main.shake(180, 0.008);
    this.cameras.main.flash(120, 255, 60, 60, false);
    this.drawHpBars();
    this.checkEnd();
  }

  private fireConfusedEnemyAttack() {
    const enemy = this.enemy();
    if (this.enemySprite && this.anims.exists(enemy.hitAnimKey)) {
      this.enemySprite.play(enemy.hitAnimKey);
      this.enemySprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
        this.enemySprite?.play(enemy.idleAnimKey);
      });
    }
    const targetRow = Math.floor(Math.random() * ROWS);
    const targetCol = Math.floor(Math.random() * COLS);
    const beam = this.add.graphics();
    const w = this.scale.width;
    const sourceX = Math.floor(w * 0.82);
    const sourceY = Math.floor(this.bannerHeight * 0.5);
    const tx = this.cellX(targetCol);
    const ty = this.cellY(targetRow);
    beam.lineStyle(8, 0xfacc15, 0.95);
    beam.beginPath();
    beam.moveTo(sourceX, sourceY);
    beam.lineTo(tx, ty);
    beam.strokePath();
    beam.fillStyle(0xfde68a, 0.8);
    beam.fillCircle(tx, ty, 24);
    this.tweens.add({
      targets: beam,
      alpha: 0,
      duration: 420,
      onComplete: () => beam.destroy(),
    });
    void this.clearCellsAround({ row: targetRow, col: targetCol }, 1);
  }

  private async clearCellsAround(center: Cell, radius: number) {
    if (this.busy) return;
    this.busy = true;
    const toClear = new Set<TileId>();
    for (let dr = -radius; dr <= radius; dr++) {
      for (let dc = -radius; dc <= radius; dc++) {
        const nr = center.row + dr;
        const nc = center.col + dc;
        if (nr < 0 || nr >= ROWS || nc < 0 || nc >= COLS) continue;
        const t = this.grid[nr][nc];
        if (t) toClear.add(t.id);
      }
    }
    await this.popAndRefill(toClear);
    await this.resolveCascades();
    this.busy = false;
    this.checkEnd();
  }

  private async popAndRefill(toClear: Set<TileId>) {
    if (toClear.size === 0) return;
    await this.popTiles(toClear);
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const t = this.grid[r][c];
        if (t && toClear.has(t.id)) this.grid[r][c] = null;
      }
    }
    const falls = applyGravity(this.grid);
    const spawns = refill(this.grid, this.allKinds);
    const fallPromises = falls.map((f) =>
      this.moveTileSprite(
        f.tile,
        f.toRow,
        f.col,
        Math.max(DUR_FALL_MIN, DUR_FALL_PER_ROW * (f.toRow - f.fromRow)),
      ),
    );
    const spawnsByCol = new Map<number, typeof spawns>();
    for (const s of spawns) {
      const list = spawnsByCol.get(s.col) ?? [];
      list.push(s);
      spawnsByCol.set(s.col, list);
    }
    const spawnPromises: Promise<void>[] = [];
    for (const [, list] of spawnsByCol) {
      const stack = list.length;
      list.forEach((s) => {
        spawnPromises.push(this.spawnTile(s.tile, s.row, s.col, s.row - stack));
      });
    }
    await Promise.all([...fallPromises, ...spawnPromises]);
  }

  private showFirstAttackBlockedBanner() {
    const w = this.scale.width;
    const y = Math.floor(this.bannerHeight * 0.5);
    const text = this.add
      .text(w / 2, y, 'BLOCKED!', {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '42px',
        color: '#fde68a',
        fontStyle: 'bold',
        stroke: '#0a0f1f',
        strokeThickness: 7,
      })
      .setOrigin(0.5)
      .setScale(0.4)
      .setAlpha(0);
    this.tweens.add({
      targets: text,
      scale: 1.2,
      alpha: 1,
      duration: 180,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: text,
          alpha: 0,
          scale: 1.5,
          duration: 500,
          delay: 300,
          onComplete: () => text.destroy(),
        });
      },
    });
  }

  private showStunnedBanner() {
    const w = this.scale.width;
    const enemyX = Math.floor(w * 0.82);
    const y = Math.floor(this.bannerHeight * 0.3);
    const text = this.add
      .text(enemyX, y, 'STUNNED!', {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '34px',
        color: '#fde68a',
        fontStyle: 'bold',
        stroke: '#0a0f1f',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setScale(0.4)
      .setAlpha(0);
    this.tweens.add({
      targets: text,
      scale: 1.1,
      alpha: 1,
      duration: 140,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: text,
          alpha: 0,
          scale: 1.3,
          duration: 500,
          delay: 240,
          ease: 'Cubic.easeOut',
          onComplete: () => text.destroy(),
        });
      },
    });
  }

  private triggerBossSpecial() {
    if (this.telegraphActive) return;
    this.telegraphActive = true;
    this.showTelegraph();
    this.telegraphTimer = this.time.delayedCall(2000, () => {
      this.hideTelegraph();
      const baseDmg = this.level.bossSpecialDamage ?? this.battle.enemyAttackDamage * 2;
      const dmg = this.desperateMode ? Math.max(1, Math.floor(baseDmg / 2)) : baseDmg;
      const enemy = this.enemy();
      if (enemy.bigAttackAnimKey && this.enemySprite && this.anims.exists(enemy.bigAttackAnimKey)) {
        this.enemySprite.play(enemy.bigAttackAnimKey);
        this.enemySprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
          this.enemySprite?.play(enemy.idleAnimKey);
        });
      }
      this.battle = damageHero(this.battle, dmg);
      this.cameras.main.shake(450, 0.02);
      this.cameras.main.flash(220, 255, 40, 40, false);
      this.drawHpBars();
      this.telegraphActive = false;
      this.checkEnd();
    });
  }

  private showTelegraph() {
    const w = this.scale.width;
    const h = this.scale.height;
    this.telegraphDimOverlay = this.add.graphics();
    this.telegraphDimOverlay.fillStyle(0x000000, 0.35);
    this.telegraphDimOverlay.fillRect(0, 0, w, h);

    const enemyX = Math.floor(w * 0.82);
    const enemyY = Math.floor(this.bannerHeight * 0.35);
    this.telegraphIcon = this.add.graphics();
    this.telegraphIcon.fillStyle(0xef4444, 1);
    this.telegraphIcon.lineStyle(4, 0xffffff, 1);
    const r = 32;
    this.telegraphIcon.fillCircle(enemyX, enemyY, r);
    this.telegraphIcon.strokeCircle(enemyX, enemyY, r);
    this.telegraphIcon.lineStyle(6, 0xffffff, 1);
    this.telegraphIcon.beginPath();
    this.telegraphIcon.moveTo(enemyX, enemyY - r * 0.5);
    this.telegraphIcon.lineTo(enemyX, enemyY + r * 0.15);
    this.telegraphIcon.moveTo(enemyX, enemyY + r * 0.4);
    this.telegraphIcon.lineTo(enemyX, enemyY + r * 0.55);
    this.telegraphIcon.strokePath();
    this.tweens.add({
      targets: this.telegraphIcon,
      scale: 1.15,
      duration: 300,
      yoyo: true,
      repeat: -1,
    });
  }

  private hideTelegraph() {
    this.telegraphTimer?.remove();
    this.telegraphTimer = undefined;
    this.telegraphDimOverlay?.destroy();
    this.telegraphDimOverlay = undefined;
    this.telegraphIcon?.destroy();
    this.telegraphIcon = undefined;
  }

  private onHeroCascadeStep(
    kind: TileKind,
    count: number,
    comboStep: number,
    multiplier: number,
  ) {
    if (count === 0) return;
    const hero = Object.values(HEROES).find((h) => h.tileKind === kind);
    if (!hero) return;
    const base = damageForMatch(count, comboStep);
    const damage = Math.max(1, Math.round(base * multiplier) + this.matchBonusDamage);
    this.battle = damageEnemy(this.battle, damage);
    playSFX('hit');

    const sprite = this.heroSprites.get(hero.id);
    if (sprite && this.anims.exists(hero.attackAnimKey)) {
      sprite.play(hero.attackAnimKey);
      sprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
        sprite.play(hero.idleAnimKey);
      });
    }

    const enemy = this.enemy();
    if (this.enemySprite && this.anims.exists(enemy.hitAnimKey)) {
      this.enemySprite.play(enemy.hitAnimKey);
      this.enemySprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
        this.enemySprite?.play(enemy.idleAnimKey);
      });
    }
    const emphasis: 'normal' | 'big' | 'teamup' =
      multiplier > 1 ? 'teamup' : damage >= 5 ? 'big' : 'normal';
    this.floatDamageNumber(damage, emphasis);
    this.drawHpBars();
  }

  private floatDamageNumber(amount: number, emphasis: 'normal' | 'big' | 'teamup') {
    if (!this.enemySprite && !this.enemyPlaceholder) return;
    const w = this.scale.width;
    const baseX = Math.floor(w * 0.82);
    const baseY = Math.floor(this.bannerHeight * 0.28);
    const jitterX = (Math.random() - 0.5) * 40;
    const color = emphasis === 'teamup' ? '#ff6b6b' : emphasis === 'big' ? '#fbbf24' : '#ffffff';
    const fontSize = emphasis === 'teamup' ? '40px' : emphasis === 'big' ? '32px' : '24px';
    const text = this.add
      .text(baseX + jitterX, baseY, `-${amount}`, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize,
        color,
        fontStyle: 'bold',
        stroke: '#0a0f1f',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setAlpha(0);
    this.tweens.add({
      targets: text,
      alpha: 1,
      scale: 1.2,
      duration: 120,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: text,
          y: baseY - 70,
          alpha: 0,
          scale: 1.0,
          duration: 650,
          ease: 'Cubic.easeOut',
          onComplete: () => text.destroy(),
        });
      },
    });
  }

  private onHeroTap(heroId: HeroId) {
    void this.firePower(heroId);
  }

  private async firePower(heroId: HeroId) {
    if (this.busy || this.paused || this.modal) return;
    if (!powerReady(this.battle, heroId)) {
      this.shakeHero(heroId);
      return;
    }
    const spec = POWERS[heroId];
    if (!spec) return;
    this.busy = true;
    this.hideHint();
    this.markInput();

    const result = spec.execute(this.grid, { rng: Math.random });

    const visual = POWER_VISUALS[heroId];
    if (visual) {
      const [fr, fg, fb] = visual.flash;
      this.cameras.main.flash(280, fr, fg, fb, false);
      this.cameras.main.shake(280, 0.014);
      this.showPowerBanner(visual.label, visual.color);
      playSFX('power-fire');
    }

    const hero = HEROES[heroId];
    const heroSprite = this.heroSprites.get(heroId);
    if (heroId === 'hulk' && heroSprite) {
      const originalY = heroSprite.y;
      this.tweens.add({
        targets: heroSprite,
        y: originalY - 30,
        duration: 180,
        ease: 'Cubic.easeOut',
        yoyo: true,
        onYoyo: () => {
          this.cameras.main.shake(320, 0.022);
        },
        onComplete: () => {
          heroSprite.y = originalY;
        },
      });
    } else if (heroSprite && this.anims.exists(hero.attackAnimKey)) {
      heroSprite.play(hero.attackAnimKey);
      heroSprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
        heroSprite.play(hero.idleAnimKey);
      });
    }

    const affectedCells: Cell[] = [
      ...result.clearedCells,
      ...result.convertedCells.map((c) => c.cell),
    ];
    if (visual && affectedCells.length > 0) {
      this.drawPowerBeams(heroId, affectedCells, visual.color, visual.fromSky ?? false);
    }

    if (result.freezeEnemyMs && result.freezeEnemyMs > 0) {
      this.battle = freezeEnemyAttack(this.battle, result.freezeEnemyMs);
      this.playTimeStopEffect(result.freezeEnemyMs);
    }

    if (result.stunAttacks && result.stunAttacks > 0) {
      this.battle = queueAttackSkip(this.battle, result.stunAttacks);
    }

    if (result.spawnBombs && result.spawnBombs.length > 0) {
      for (const cell of result.spawnBombs) {
        const tile = this.grid[cell.row][cell.col];
        if (!tile || tile.bomb) continue;
        tile.bomb = true;
        const sprite = this.sprites.get(tile.id);
        if (sprite) {
          const gg = sprite.first as Phaser.GameObjects.Graphics | null;
          if (gg) this.paintTile(gg, tile, this.tileSize);
        }
      }
      this.cameras.main.shake(200, 0.012);
    }

    for (const { cell, newKind } of result.convertedCells) {
      const tile = this.grid[cell.row][cell.col];
      if (!tile) continue;
      tile.kind = newKind;
      const tileSprite = this.sprites.get(tile.id);
      if (tileSprite) {
        const g = tileSprite.first as Phaser.GameObjects.Graphics | null;
        if (g) this.drawTileGraphics(g, newKind, this.tileSize);
      }
    }

    if (result.clearedCells.length > 0) {
      const toClear = new Set<TileId>();
      for (const cell of result.clearedCells) {
        const tile = this.grid[cell.row][cell.col];
        if (tile) toClear.add(tile.id);
      }
      await this.popTiles(toClear);
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          const t = this.grid[r][c];
          if (t && toClear.has(t.id)) this.grid[r][c] = null;
        }
      }
      const falls = applyGravity(this.grid);
      const spawns = refill(this.grid, this.allKinds);
      const fallPromises = falls.map((f) =>
        this.moveTileSprite(
          f.tile,
          f.toRow,
          f.col,
          Math.max(DUR_FALL_MIN, DUR_FALL_PER_ROW * (f.toRow - f.fromRow)),
        ),
      );
      const spawnsByCol = new Map<number, typeof spawns>();
      for (const s of spawns) {
        const list = spawnsByCol.get(s.col) ?? [];
        list.push(s);
        spawnsByCol.set(s.col, list);
      }
      const spawnPromises: Promise<void>[] = [];
      for (const [, list] of spawnsByCol) {
        const stack = list.length;
        list.forEach((s) => {
          spawnPromises.push(this.spawnTile(s.tile, s.row, s.col, s.row - stack));
        });
      }
      await Promise.all([...fallPromises, ...spawnPromises]);
    }

    if (spec.bonusDamage > 0) {
      this.battle = damageEnemy(this.battle, spec.bonusDamage);
      this.floatDamageNumber(spec.bonusDamage, 'big');
      this.drawHpBars();
    }

    this.battle = consumePower(this.battle, heroId);
    this.positionAndRedrawBattleUi();

    await this.resolveCascades();
    this.busy = false;
    this.checkEnd();
  }

  private shakeHero(heroId: HeroId) {
    const sprite = this.heroSprites.get(heroId);
    const ring = this.heroRings.get(heroId);
    if (!sprite && !ring) return;
    if (sprite) {
      const originX = sprite.x;
      this.tweens.add({
        targets: sprite,
        x: originX + 6,
        duration: 60,
        yoyo: true,
        repeat: 1,
        onComplete: () => {
          sprite.x = originX;
        },
      });
    }
    if (ring) {
      const flash = this.add.graphics();
      const w = this.scale.width;
      const ringCx = this.heroSlotX(this.team.indexOf(heroId), w);
      const spriteScale = (this.bannerHeight * 0.42) / 92;
      const ringRadius = Math.max(22, 34 * spriteScale);
      const ringCy = Math.floor(this.bannerHeight * 0.66) - Math.max(22, 30 * spriteScale);
      flash.lineStyle(6, 0xef4444, 1);
      flash.strokeCircle(ringCx, ringCy, ringRadius + 2);
      this.tweens.add({
        targets: flash,
        alpha: 0,
        duration: 350,
        ease: 'Cubic.easeOut',
        onComplete: () => flash.destroy(),
      });
    }
  }

  private createStoneStrip() {
    const save = loadSave();
    const equipped = resolveEquippedStone(save);
    if (!equipped) return;
    const container = this.add.container(0, 0);
    const spec = STONES[equipped];

    const bg = this.add.graphics();
    bg.fillStyle(spec.color, 1);
    bg.lineStyle(3, 0x0a0f1f, 1);
    bg.fillCircle(0, 0, 18);
    bg.strokeCircle(0, 0, 18);
    bg.fillStyle(0xffffff, 0.35);
    bg.fillCircle(-5, -5, 5);
    container.add(bg);

    const glyph = this.add
      .text(0, 0, spec.name[0], {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '18px',
        color: '#0a0f1f',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    container.add(glyph);

    const hit = this.add.zone(0, 0, 48, 48).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => this.fireStone(equipped));
    container.add(hit);

    this.stoneButtons.set(equipped, container);
    this.repositionStoneStrip();
  }

  private repositionStoneStrip() {
    if (this.stoneButtons.size === 0) return;
    const w = this.scale.width;
    const stripY = this.bannerHeight + this.stoneStripHeight / 2;
    const count = this.stoneButtons.size;
    const spacing = 48;
    const totalW = count * spacing;
    const startX = Math.floor((w - totalW) / 2 + spacing / 2);
    let i = 0;
    for (const container of this.stoneButtons.values()) {
      container.setPosition(startX + i * spacing, stripY);
      i++;
    }
    this.updateStoneButtonsUsedState();
  }

  private updateStoneButtonsUsedState() {
    for (const [id, container] of this.stoneButtons) {
      const used = this.battle.usedStones.includes(id);
      container.setAlpha(used ? 0.35 : 1);
    }
  }

  private fireStone(id: InfinityStoneId) {
    if (this.busy || this.paused || this.modal) return;
    if (this.battle.phase !== 'playing') return;
    if (this.battle.usedStones.includes(id)) return;
    this.battle = markStoneUsed(this.battle, id);
    this.updateStoneButtonsUsedState();

    const spec = STONES[id];
    const [fr, fg, fb] = spec.flash;
    this.cameras.main.flash(400, fr, fg, fb, false);
    this.cameras.main.shake(380, 0.02);
    this.showPowerBanner(spec.label, spec.color);
    playSFX('power-fire');

    switch (id) {
      case 'power':
        void this.fireStonePower();
        return;
      case 'mind':
        this.fireStoneMind();
        return;
      case 'reality':
        void this.fireStoneReality();
        return;
      case 'space':
        this.fireStoneSpace();
        return;
      case 'time':
        this.fireStoneTime();
        return;
      case 'soul':
        this.fireStoneSoul();
        return;
      case 'morphin-blast':
        void this.fireStoneMorphinBlast();
        return;
      case 'zord-strike':
        void this.fireStoneZordStrike();
        return;
    }
  }

  private async fireStoneZordStrike() {
    const toClear = new Set<TileId>();
    const len = Math.min(ROWS, COLS);
    for (let i = 0; i < len; i++) {
      const t = this.grid[i][i];
      if (t) toClear.add(t.id);
      const t2 = this.grid[i][COLS - 1 - i];
      if (t2) toClear.add(t2.id);
    }
    this.busy = true;
    this.battle = damageEnemy(this.battle, 18);
    this.floatDamageNumber(18, 'big');
    this.drawHpBars();
    await this.popAndRefill(toClear);
    await this.resolveCascades();
    this.busy = false;
    this.checkEnd();
  }

  private async fireStoneMorphinBlast() {
    const toClear = new Set<TileId>();
    for (let r = 0; r < 2; r++) {
      for (let c = 0; c < COLS; c++) {
        const t = this.grid[r][c];
        if (t) toClear.add(t.id);
      }
    }
    this.busy = true;
    this.battle = damageEnemy(this.battle, 8);
    this.floatDamageNumber(8, 'big');
    this.drawHpBars();
    this.battle = queueAttackSkip(this.battle, 1);
    await this.popAndRefill(toClear);
    await this.resolveCascades();
    this.busy = false;
    this.checkEnd();
  }

  private async fireStonePower() {
    const toClear = new Set<TileId>();
    const centerCols = [Math.floor(COLS / 2) - 1, Math.floor(COLS / 2), Math.floor(COLS / 2) + 1];
    for (const c of centerCols) {
      if (c < 0 || c >= COLS) continue;
      for (let r = 0; r < ROWS; r++) {
        const t = this.grid[r][c];
        if (t) toClear.add(t.id);
      }
    }
    this.busy = true;
    this.battle = damageEnemy(this.battle, 10);
    this.floatDamageNumber(10, 'big');
    this.drawHpBars();
    await this.popAndRefill(toClear);
    await this.resolveCascades();
    this.busy = false;
    this.checkEnd();
  }

  private fireStoneMind() {
    this.battle = { ...this.battle, confusedAttacks: 3 };
    const w = this.scale.width;
    const enemyX = Math.floor(w * 0.82);
    const enemyY = Math.floor(this.bannerHeight * 0.45);
    const halo = this.add.graphics();
    halo.lineStyle(4, 0xfacc15, 0.9);
    halo.strokeCircle(enemyX, enemyY, 56);
    halo.lineStyle(3, 0xfde68a, 0.6);
    halo.strokeCircle(enemyX, enemyY, 72);
    this.tweens.add({
      targets: halo,
      alpha: 0.3,
      duration: 600,
      yoyo: true,
      repeat: 6,
      onComplete: () => halo.destroy(),
    });
  }

  private async fireStoneReality() {
    this.busy = true;
    const heroKinds = this.heroKinds;
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const t = this.grid[r][c];
        if (!t) continue;
        const kind = heroKinds[Math.floor(Math.random() * heroKinds.length)];
        t.kind = kind;
        const sprite = this.sprites.get(t.id);
        if (sprite) {
          const g = sprite.first as Phaser.GameObjects.Graphics | null;
          if (g) this.paintTile(g, t, this.tileSize);
        }
      }
    }
    await this.resolveCascades();
    this.busy = false;
    this.checkEnd();
  }

  private fireStoneSpace() {
    const w = this.scale.width;
    const enemyX = Math.floor(w * 0.82);
    const enemyY = Math.floor(this.bannerHeight * 0.45);
    const portal = this.add.graphics();
    portal.fillStyle(0x3b82f6, 0.8);
    portal.fillCircle(enemyX, enemyY, 70);
    portal.lineStyle(5, 0x93c5fd, 1);
    portal.strokeCircle(enemyX, enemyY, 70);
    this.tweens.add({
      targets: portal,
      scale: 1.3,
      alpha: 0,
      duration: 900,
      onComplete: () => portal.destroy(),
    });
    if (this.enemySprite) {
      const origAlpha = this.enemySprite.alpha;
      this.enemySprite.setAlpha(0.35);
      this.tweens.add({
        targets: this.enemySprite,
        angle: 720,
        duration: 900,
        onComplete: () => {
          this.enemySprite?.setAlpha(origAlpha);
          if (this.enemySprite) this.enemySprite.angle = 0;
        },
      });
    }
    this.battle = damageEnemy(this.battle, 15);
    this.battle = queueAttackSkip(this.battle, 3);
    this.floatDamageNumber(15, 'big');
    this.drawHpBars();
    this.checkEnd();
  }

  private fireStoneTime() {
    this.battle = { ...this.battle, heroHp: this.battle.heroMaxHp };
    this.drawHpBars();
    const w = this.scale.width;
    const h = this.scale.height;
    const clock = this.add.graphics();
    clock.setPosition(w / 2, h * 0.4);
    clock.lineStyle(6, 0x22c55e, 0.9);
    clock.strokeCircle(0, 0, 60);
    clock.lineStyle(5, 0xdcfce7, 1);
    clock.beginPath();
    clock.moveTo(0, 0);
    clock.lineTo(0, -46);
    clock.strokePath();
    this.tweens.add({
      targets: clock,
      angle: -720,
      alpha: 0,
      duration: 900,
      onComplete: () => clock.destroy(),
    });
  }

  private fireStoneSoul() {
    for (const id of this.team) {
      this.battle = addPowerCharge(this.battle, id, POWER_MAX_CHARGES);
    }
    this.positionAndRedrawBattleUi();
  }

  private showPowerBanner(label: string, color: number) {
    const w = this.scale.width;
    const h = this.scale.height;
    const y = Math.floor(h * 0.45);
    const hex = '#' + color.toString(16).padStart(6, '0');
    const text = this.add
      .text(w / 2, y, label, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '58px',
        color: hex,
        fontStyle: 'bold',
        stroke: '#0a0f1f',
        strokeThickness: 8,
      })
      .setOrigin(0.5)
      .setScale(0.3)
      .setAlpha(0)
      .setAngle(-4);
    this.tweens.add({
      targets: text,
      scale: 1.3,
      alpha: 1,
      angle: 0,
      duration: 180,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: text,
          scale: 1.55,
          alpha: 0,
          duration: 560,
          delay: 360,
          ease: 'Cubic.easeOut',
          onComplete: () => text.destroy(),
        });
      },
    });
  }

  private drawPowerBeams(heroId: HeroId, cells: Cell[], color: number, fromSky: boolean) {
    const hero = this.heroSprites.get(heroId);
    const w = this.scale.width;
    const sourceX = hero?.x ?? Math.floor(w * 0.2);
    const sourceY = (hero?.y ?? 0) - 40;

    cells.forEach((cell, idx) => {
      const tx = this.cellX(cell.col);
      const ty = this.cellY(cell.row);
      const sx = fromSky ? tx : sourceX;
      const sy = fromSky ? -20 : sourceY;

      const beam = this.add.graphics();
      beam.lineStyle(8, color, 0.95);
      beam.beginPath();
      beam.moveTo(sx, sy);
      beam.lineTo(tx, ty);
      beam.strokePath();
      beam.lineStyle(3, 0xffffff, 0.9);
      beam.beginPath();
      beam.moveTo(sx, sy);
      beam.lineTo(tx, ty);
      beam.strokePath();
      beam.fillStyle(color, 0.6);
      beam.fillCircle(tx, ty, 18);
      beam.fillStyle(0xffffff, 0.9);
      beam.fillCircle(tx, ty, 8);

      beam.setAlpha(0);
      this.tweens.add({
        targets: beam,
        alpha: 1,
        duration: 80,
        delay: idx * 35,
        onComplete: () => {
          this.tweens.add({
            targets: beam,
            alpha: 0,
            duration: 360,
            ease: 'Cubic.easeOut',
            onComplete: () => beam.destroy(),
          });
        },
      });

      const burst = this.add.graphics();
      burst.fillStyle(color, 1);
      burst.fillCircle(tx, ty, 4);
      burst.setScale(0);
      this.tweens.add({
        targets: burst,
        scale: 5,
        alpha: 0,
        duration: 420,
        delay: idx * 35,
        ease: 'Cubic.easeOut',
        onComplete: () => burst.destroy(),
      });
    });
  }

  private drawBeamsFromPoint(sourceX: number, sourceY: number, cells: Cell[], color: number) {
    cells.forEach((cell, idx) => {
      const tx = this.cellX(cell.col);
      const ty = this.cellY(cell.row);
      const beam = this.add.graphics();
      beam.lineStyle(8, color, 0.95);
      beam.beginPath();
      beam.moveTo(sourceX, sourceY);
      beam.lineTo(tx, ty);
      beam.strokePath();
      beam.lineStyle(3, 0xffffff, 0.9);
      beam.beginPath();
      beam.moveTo(sourceX, sourceY);
      beam.lineTo(tx, ty);
      beam.strokePath();
      beam.fillStyle(color, 0.6);
      beam.fillCircle(tx, ty, 18);
      beam.fillStyle(0xffffff, 0.9);
      beam.fillCircle(tx, ty, 8);
      beam.setAlpha(0);
      this.tweens.add({
        targets: beam,
        alpha: 1,
        duration: 80,
        delay: idx * 30,
        onComplete: () => {
          this.tweens.add({
            targets: beam,
            alpha: 0,
            duration: 360,
            ease: 'Cubic.easeOut',
            onComplete: () => beam.destroy(),
          });
        },
      });
    });
  }

  private updateTeamUpSparks() {
    const w = this.scale.width;
    const spriteScale = (this.bannerHeight * 0.42) / 92;
    const ringRadius = Math.max(22, 34 * spriteScale);
    const ringCy = Math.floor(this.bannerHeight * 0.66) - Math.max(22, 30 * spriteScale);
    const sparkY = ringCy - ringRadius - 14;

    type Desired = {
      key: string;
      state: 'active' | 'ghost';
      x: number;
      spec: TeamUpSpec;
      a: HeroId;
      b: HeroId;
      partnerCharge: number;
    };
    const wanted = new Map<string, Desired>();

    if (!this.paused && this.battle.phase === 'playing') {
      for (let i = 0; i < this.team.length; i++) {
        for (let j = i + 1; j < this.team.length; j++) {
          const a = this.team[i];
          const b = this.team[j];
          const readyA = powerReady(this.battle, a);
          const readyB = powerReady(this.battle, b);
          if (!readyA && !readyB) continue;
          const key = pairKey(a, b);
          const spec = resolveTeamUp(a, b);
          const midX = Math.floor((this.heroSlotX(i, w) + this.heroSlotX(j, w)) / 2);
          const state: 'active' | 'ghost' = readyA && readyB ? 'active' : 'ghost';
          const partnerCharge = readyA
            ? this.battle.powerMeters[b] ?? 0
            : this.battle.powerMeters[a] ?? 0;
          wanted.set(key, { key, state, x: midX, spec, a, b, partnerCharge });
        }
      }
    }

    for (const [key, ent] of wanted) {
      const existing = this.teamUpSparks.get(key);
      const existingState = this.teamUpSparkStates.get(key);
      if (existing && existingState === ent.state) {
        existing.setPosition(ent.x, sparkY);
        if (ent.state === 'ghost') {
          const label = existing.getByName('ghost-charge-label') as Phaser.GameObjects.Text | null;
          if (label) label.setText(`${ent.partnerCharge}/${POWER_MAX_CHARGES}`);
        }
      } else {
        if (existing) {
          this.teamUpSparkTweens.get(key)?.stop();
          this.teamUpSparkTweens.delete(key);
          existing.destroy(true);
          this.teamUpSparks.delete(key);
        }
        if (ent.state === 'active') {
          this.createTeamUpSpark(key, ent.spec, ent.x, sparkY, ent.a, ent.b);
        } else {
          this.createGhostSpark(key, ent.spec, ent.x, sparkY, ent.partnerCharge);
        }
        this.teamUpSparkStates.set(key, ent.state);
      }
    }

    for (const [key, container] of Array.from(this.teamUpSparks.entries())) {
      if (!wanted.has(key)) {
        this.teamUpSparkTweens.get(key)?.stop();
        this.teamUpSparkTweens.delete(key);
        container.destroy(true);
        this.teamUpSparks.delete(key);
        this.teamUpSparkStates.delete(key);
      }
    }

    const hasActive = Array.from(wanted.values()).some((e) => e.state === 'active');
    if (hasActive && !this.teamUpIntroShown && !this.busy && !this.modal) {
      this.showTeamUpIntro();
    }
  }

  private createGhostSpark(
    key: string,
    spec: TeamUpSpec,
    x: number,
    y: number,
    partnerCharge: number,
  ) {
    const container = this.add.container(x, y);
    container.setDepth(90);

    const burst = this.add.graphics();
    burst.fillStyle(spec.accentColor, 0.2);
    burst.lineStyle(2, spec.accentColor, 0.45);
    const outer = 14;
    const inner = 6;
    const points: { x: number; y: number }[] = [];
    for (let i = 0; i < 16; i++) {
      const r = i % 2 === 0 ? outer : inner;
      const a = -Math.PI / 2 + (i * Math.PI) / 8;
      points.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
    }
    burst.beginPath();
    burst.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) burst.lineTo(points[i].x, points[i].y);
    burst.closePath();
    burst.fillPath();
    burst.strokePath();
    container.add(burst);

    const label = this.add
      .text(0, 22, `${partnerCharge}/${POWER_MAX_CHARGES}`, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '11px',
        color: '#94a3b8',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    label.setName('ghost-charge-label');
    container.add(label);

    container.setAlpha(0);
    this.tweens.add({
      targets: container,
      alpha: 0.85,
      duration: 220,
      ease: 'Cubic.easeOut',
    });

    this.teamUpSparks.set(key, container);
  }

  private createTeamUpSpark(
    key: string,
    spec: TeamUpSpec,
    x: number,
    y: number,
    heroA: HeroId,
    heroB: HeroId,
  ) {
    const container = this.add.container(x, y);
    container.setDepth(100);

    const glow = this.add.graphics();
    glow.fillStyle(spec.accentColor, 0.35);
    glow.fillCircle(0, 0, 26);
    container.add(glow);

    const burst = this.add.graphics();
    burst.fillStyle(spec.accentColor, 1);
    burst.lineStyle(2, 0xffd24a, 1);
    const outer = 18;
    const inner = 8;
    const points: { x: number; y: number }[] = [];
    for (let i = 0; i < 16; i++) {
      const r = i % 2 === 0 ? outer : inner;
      const a = -Math.PI / 2 + (i * Math.PI) / 8;
      points.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
    }
    burst.beginPath();
    burst.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) burst.lineTo(points[i].x, points[i].y);
    burst.closePath();
    burst.fillPath();
    burst.strokePath();
    burst.fillStyle(0xffffff, 0.9);
    burst.fillCircle(0, 0, 4);
    container.add(burst);

    const zone = this.add.zone(0, 0, 64, 64).setInteractive({ useHandCursor: true });
    zone.on('pointerup', () => this.onTeamUpTap(heroA, heroB));
    container.add(zone);

    container.setScale(0.2).setAlpha(0);
    this.tweens.add({
      targets: container,
      scale: 1,
      alpha: 1,
      duration: 260,
      ease: 'Back.easeOut',
    });
    const pulseTween = this.tweens.add({
      targets: burst,
      scale: 1.18,
      duration: 420,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.teamUpSparkTweens.set(key, pulseTween);

    this.teamUpSparks.set(key, container);
  }

  private onTeamUpTap(heroA: HeroId, heroB: HeroId) {
    void this.fireTeamUp(heroA, heroB);
  }

  private async fireTeamUp(heroA: HeroId, heroB: HeroId) {
    if (this.busy || this.paused || this.modal) return;
    if (!powerReady(this.battle, heroA) || !powerReady(this.battle, heroB)) return;

    const spec = resolveTeamUp(heroA, heroB);
    this.busy = true;
    this.hideHint();
    this.markInput();

    const result = spec.execute(this.grid, { rng: Math.random });

    const [fr, fg, fb] = spec.flash;
    this.cameras.main.flash(340, fr, fg, fb, false);
    this.cameras.main.shake(result.extraShake ? 420 : 280, result.extraShake ? 0.022 : 0.016);
    this.showPowerBanner(`${spec.displayName.toUpperCase()}!`, spec.accentColor);
    playSFX('power-fire');

    for (const heroId of [heroA, heroB]) {
      const hero = HEROES[heroId];
      const sprite = this.heroSprites.get(heroId);
      if (sprite && this.anims.exists(hero.attackAnimKey)) {
        sprite.play(hero.attackAnimKey);
        sprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
          sprite.play(hero.idleAnimKey);
        });
      }
    }

    const affectedCells: Cell[] = [
      ...result.clearedCells,
      ...result.convertedCells.map((c) => c.cell),
      ...(result.spawnBombs ?? []),
    ];
    if (affectedCells.length > 0) {
      const spriteA = this.heroSprites.get(heroA);
      const spriteB = this.heroSprites.get(heroB);
      const sx = Math.floor(((spriteA?.x ?? 0) + (spriteB?.x ?? 0)) / 2);
      const sy = Math.floor(((spriteA?.y ?? 0) + (spriteB?.y ?? 0)) / 2) - 40;
      this.drawBeamsFromPoint(sx, sy, affectedCells, spec.accentColor);
    }

    if (result.freezeEnemyMs && result.freezeEnemyMs > 0) {
      this.battle = freezeEnemyAttack(this.battle, result.freezeEnemyMs);
      this.playTimeStopEffect(result.freezeEnemyMs);
    }

    if (result.stunAttacks && result.stunAttacks > 0) {
      this.battle = queueAttackSkip(this.battle, result.stunAttacks);
    }

    if (result.firstAttackBlocked) {
      this.battle = { ...this.battle, firstAttackFreeRemaining: true };
    }

    if (result.spawnBombs && result.spawnBombs.length > 0) {
      for (const cell of result.spawnBombs) {
        const tile = this.grid[cell.row][cell.col];
        if (!tile || tile.bomb) continue;
        tile.bomb = true;
        const sprite = this.sprites.get(tile.id);
        if (sprite) {
          const gg = sprite.first as Phaser.GameObjects.Graphics | null;
          if (gg) this.paintTile(gg, tile, this.tileSize);
        }
      }
      this.cameras.main.shake(200, 0.012);
    }

    for (const { cell, newKind } of result.convertedCells) {
      const tile = this.grid[cell.row][cell.col];
      if (!tile) continue;
      tile.kind = newKind;
      const tileSprite = this.sprites.get(tile.id);
      if (tileSprite) {
        const g = tileSprite.first as Phaser.GameObjects.Graphics | null;
        if (g) this.drawTileGraphics(g, newKind, this.tileSize);
      }
    }

    if (result.clearedCells.length > 0) {
      const toClear = new Set<TileId>();
      for (const cell of result.clearedCells) {
        const tile = this.grid[cell.row][cell.col];
        if (tile) toClear.add(tile.id);
      }
      await this.popTiles(toClear);
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          const t = this.grid[r][c];
          if (t && toClear.has(t.id)) this.grid[r][c] = null;
        }
      }
      const falls = applyGravity(this.grid);
      const spawns = refill(this.grid, this.allKinds);
      const fallPromises = falls.map((f) =>
        this.moveTileSprite(
          f.tile,
          f.toRow,
          f.col,
          Math.max(DUR_FALL_MIN, DUR_FALL_PER_ROW * (f.toRow - f.fromRow)),
        ),
      );
      const spawnsByCol = new Map<number, typeof spawns>();
      for (const s of spawns) {
        const list = spawnsByCol.get(s.col) ?? [];
        list.push(s);
        spawnsByCol.set(s.col, list);
      }
      const spawnPromises: Promise<void>[] = [];
      for (const [, list] of spawnsByCol) {
        const stack = list.length;
        list.forEach((s) => {
          spawnPromises.push(this.spawnTile(s.tile, s.row, s.col, s.row - stack));
        });
      }
      await Promise.all([...fallPromises, ...spawnPromises]);
    }

    if (spec.bonusDamage > 0) {
      this.battle = damageEnemy(this.battle, spec.bonusDamage);
      this.floatDamageNumber(spec.bonusDamage, 'big');
      this.drawHpBars();
    }

    this.battle = consumePower(this.battle, heroA);
    this.battle = consumePower(this.battle, heroB);
    this.positionAndRedrawBattleUi();

    await this.resolveCascades();
    this.busy = false;
    this.checkEnd();
  }

  private showTeamUpIntro() {
    this.teamUpIntroShown = true;
    markTutorialSeen('team-up-intro');

    const w = this.scale.width;
    const h = this.scale.height;
    const container = this.add.container(0, 0);
    container.setDepth(200);

    const dim = this.add.graphics();
    dim.fillStyle(0x000000, 0.6);
    dim.fillRect(0, 0, w, h);
    container.add(dim);

    const banner = this.add
      .text(w / 2, h * 0.32, 'TEAM UP!', {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '56px',
        color: '#ffd24a',
        fontStyle: 'bold',
        stroke: '#0a0f1f',
        strokeThickness: 10,
      })
      .setOrigin(0.5)
      .setScale(0.3)
      .setAlpha(0);
    container.add(banner);

    const subtitle = this.add
      .text(w / 2, h * 0.42, 'Tap the sparkle to unleash a combo!', {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '22px',
        color: '#ffffff',
        fontStyle: 'bold',
        stroke: '#0a0f1f',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setAlpha(0);
    container.add(subtitle);

    const firstSpark = Array.from(this.teamUpSparks.values())[0];
    let finger: Phaser.GameObjects.Text | undefined;
    if (firstSpark) {
      finger = this.add
        .text(firstSpark.x, firstSpark.y + 56, '👇', {
          fontSize: '48px',
        })
        .setOrigin(0.5);
      finger.setDepth(201);
      this.tweens.add({
        targets: finger,
        y: firstSpark.y + 72,
        duration: 360,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    this.tweens.add({
      targets: banner,
      scale: 1,
      alpha: 1,
      duration: 300,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: subtitle,
      alpha: 1,
      duration: 300,
      delay: 150,
    });

    this.time.delayedCall(2200, () => {
      this.tweens.add({
        targets: [container, finger].filter(Boolean) as Phaser.GameObjects.GameObject[],
        alpha: 0,
        duration: 260,
        onComplete: () => {
          container.destroy(true);
          finger?.destroy();
        },
      });
    });
  }

  private playTimeStopEffect(durationMs: number) {
    const w = this.scale.width;

    const enemyX = Math.floor(w * 0.82);
    const enemyY = Math.floor(this.bannerHeight * 0.4);

    const sigil = this.add.graphics();
    sigil.setPosition(enemyX, enemyY);
    const outerR = 46;
    const innerR = 28;
    sigil.lineStyle(4, 0x86efac, 1);
    sigil.strokeCircle(0, 0, outerR);
    sigil.lineStyle(3, 0x22c55e, 1);
    sigil.strokeCircle(0, 0, innerR);
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      const x1 = Math.cos(a) * (innerR + 3);
      const y1 = Math.sin(a) * (innerR + 3);
      const x2 = Math.cos(a) * (outerR - 3);
      const y2 = Math.sin(a) * (outerR - 3);
      sigil.lineStyle(2, 0x86efac, 0.85);
      sigil.beginPath();
      sigil.moveTo(x1, y1);
      sigil.lineTo(x2, y2);
      sigil.strokePath();
    }
    sigil.lineStyle(4, 0xfde68a, 1);
    sigil.beginPath();
    sigil.moveTo(0, 0);
    sigil.lineTo(0, -innerR * 0.8);
    sigil.strokePath();
    sigil.lineStyle(3, 0xfde68a, 1);
    sigil.beginPath();
    sigil.moveTo(0, 0);
    sigil.lineTo(innerR * 0.55, 0);
    sigil.strokePath();

    this.tweens.add({
      targets: sigil,
      angle: 360,
      duration: 1800,
      repeat: Math.max(1, Math.floor(durationMs / 1800)),
    });
    this.tweens.add({
      targets: sigil,
      alpha: { from: 1, to: 0 },
      duration: 600,
      delay: durationMs - 600,
      onComplete: () => sigil.destroy(),
    });

    const aura = this.add.graphics();
    aura.setPosition(enemyX, enemyY);
    aura.fillStyle(0x86efac, 0.25);
    aura.fillCircle(0, 0, 64);
    aura.lineStyle(3, 0x86efac, 0.8);
    aura.strokeCircle(0, 0, 64);
    this.tweens.add({
      targets: aura,
      alpha: { from: 0.9, to: 0.35 },
      scale: 1.1,
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: aura,
      alpha: 0,
      duration: 400,
      delay: durationMs - 400,
      onComplete: () => aura.destroy(),
    });
  }

  private showTeamUpBanner() {
    const w = this.scale.width;
    const h = this.scale.height;
    const y = Math.floor(h * 0.45);
    const text = this.add
      .text(w / 2, y, 'TEAM-UP!', {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '48px',
        color: '#ffd24a',
        fontStyle: 'bold',
        stroke: '#0a0f1f',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setScale(0.4)
      .setAlpha(0);
    this.tweens.add({
      targets: text,
      scale: 1.15,
      alpha: 1,
      duration: 180,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: text,
          scale: 1.4,
          alpha: 0,
          duration: 550,
          delay: 280,
          ease: 'Cubic.easeOut',
          onComplete: () => text.destroy(),
        });
      },
    });
  }

  private checkEnd() {
    if (this.battle.phase === 'won') {
      const stars = computeStars(this.battle);
      recordStars(this.level.id, stars);
      clearFailures(this.level.id);
      this.hideHint();
      playSFX('victory');
      this.showVictoryModal(stars, 0, []);
    } else if (this.battle.phase === 'lost') {
      recordFailure(this.level.id);
      this.hideHint();
      playSFX('defeat');
      this.showRetryModal();
    }
  }

  private cellX(col: number): number {
    return this.boardOriginX + col * this.tileSize + this.tileSize / 2;
  }
  private cellY(row: number): number {
    return this.boardOriginY + row * this.tileSize + this.tileSize / 2;
  }

  private renderInitialGrid() {
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const tile = this.grid[r][c];
        if (tile) this.createSpriteFor(tile, r, c);
      }
    }
  }

  private createSpriteFor(tile: Tile, row: number, col: number): Phaser.GameObjects.Container {
    const container = this.add.container(this.cellX(col), this.cellY(row));
    const g = this.add.graphics();
    this.paintTile(g, tile, this.tileSize);
    container.add(g);
    container.setSize(this.tileSize, this.tileSize);
    container.setData('tileId', tile.id);
    container.setInteractive(
      new Phaser.Geom.Rectangle(-this.tileSize / 2, -this.tileSize / 2, this.tileSize, this.tileSize),
      Phaser.Geom.Rectangle.Contains,
    );
    this.boardContainer.add(container);
    this.sprites.set(tile.id, container);
    return container;
  }

  private paintTile(g: Phaser.GameObjects.Graphics, tile: Tile, size: number) {
    this.drawTileGraphics(g, tile.kind, size);
    if (tile.bomb) this.drawBombBadge(g, size);
    if (tile.portal) this.drawPortalBadge(g, size);
    if (tile.gravity) this.drawGravityBadge(g, size);
  }

  private drawGravityBadge(g: Phaser.GameObjects.Graphics, size: number) {
    const cx = 0;
    const cy = 0;
    const r = size * 0.36;
    g.lineStyle(3, 0xffffff, 0.9);
    g.strokeCircle(cx, cy, r);
    g.lineStyle(2, 0xffffff, 0.7);
    g.strokeCircle(cx, cy, r * 0.75);
    g.lineStyle(1, 0xffffff, 0.5);
    g.strokeCircle(cx, cy, r * 0.55);
    g.fillStyle(0x0a0f1f, 1);
    g.fillCircle(cx, cy, r * 0.35);
    g.fillStyle(0x6366f1, 1);
    g.fillCircle(cx, cy, r * 0.18);
  }

  private drawPortalBadge(g: Phaser.GameObjects.Graphics, size: number) {
    const cx = 0;
    const cy = 0;
    const rOuter = size * 0.36;
    const rInner = size * 0.18;
    g.lineStyle(3, 0xa855f7, 0.9);
    g.strokeCircle(cx, cy, rOuter);
    g.lineStyle(2, 0x60a5fa, 0.85);
    g.strokeCircle(cx, cy, rOuter * 0.78);
    const points: { x: number; y: number }[] = [];
    for (let i = 0; i < 16; i++) {
      const r = i % 2 === 0 ? rInner : rInner * 0.55;
      const a = (i / 16) * Math.PI * 2;
      points.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
    }
    g.fillStyle(0xc084fc, 0.85);
    g.lineStyle(2, 0xfef3c7, 0.9);
    g.beginPath();
    g.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) g.lineTo(points[i].x, points[i].y);
    g.closePath();
    g.fillPath();
    g.strokePath();
    g.fillStyle(0xffffff, 0.9);
    g.fillCircle(cx, cy, rInner * 0.35);
  }

  private drawBombBadge(g: Phaser.GameObjects.Graphics, size: number) {
    const r = size * 0.18;
    const cx = size * 0.28;
    const cy = -size * 0.28;
    g.fillStyle(0x1c1917, 1);
    g.lineStyle(2, 0x0a0f1f, 1);
    g.fillCircle(cx, cy, r);
    g.strokeCircle(cx, cy, r);
    g.fillStyle(0xef4444, 1);
    g.fillCircle(cx, cy, r * 0.6);
    g.fillStyle(0xfbbf24, 1);
    g.fillCircle(cx + r * 0.6, cy - r * 0.8, r * 0.32);
    g.fillStyle(0xffffff, 1);
    g.fillCircle(cx + r * 0.5, cy - r * 0.95, r * 0.12);
  }

  private drawTileGraphics(g: Phaser.GameObjects.Graphics, kind: TileKind, size: number) {
    switch (kind) {
      case 'shield':
        drawShield(g, size);
        return;
      case 'claws':
        drawClaws(g, size);
        return;
      case 'repulsor':
        drawRepulsor(g, size);
        return;
      case 'web':
        drawWeb(g, size);
        return;
      case 'hammer':
        drawHammer(g, size);
        return;
      case 'fist':
        drawFist(g, size);
        return;
      case 'eye':
        drawEye(g, size);
        return;
      case 'hex':
        drawHex(g, size);
        return;
      case 'photon':
        drawPhoton(g, size);
        return;
      case 'gem':
        drawGem(g, size);
        return;
      case 'atom':
        drawAtom(g, size);
        return;
      case 'ghost-web':
        drawGhostWeb(g, size);
        return;
      case 'bolt':
        drawBolt(g, size);
        return;
      case 'adamantium':
        drawAdamantium(g, size);
        return;
      case 'blaster':
        drawBlaster(g, size);
        return;
      case 'billyclub':
        drawBillyclub(g, size);
        return;
      case 'flame':
        drawFlame(g, size);
        return;
      case 'power-sword':
        drawPowerSword(g, size);
        return;
      case 'dragon-dagger':
        drawDragonDagger(g, size);
        return;
      case 'power-bow':
        drawPowerBow(g, size);
        return;
    }
    g.clear();
    const fill = TILE_FILL[kind];
    if (fill === undefined) return;
    const pad = Math.max(4, Math.floor(size * 0.08));
    const radius = (size - pad * 2) / 2;
    g.lineStyle(Math.max(2, Math.floor(size * 0.04)), TILE_STROKE, 1);
    g.fillStyle(fill, 1);
    switch (kind) {
      case 'red':
        g.fillCircle(0, 0, radius);
        g.strokeCircle(0, 0, radius);
        break;
      case 'blue':
        drawRegularPolygon(g, 0, 0, radius, 3, -Math.PI / 2);
        break;
      case 'green': {
        const s = radius * 1.7;
        g.fillRoundedRect(-s / 2, -s / 2, s, s, Math.floor(size * 0.08));
        g.strokeRoundedRect(-s / 2, -s / 2, s, s, Math.floor(size * 0.08));
        break;
      }
      case 'yellow':
        drawRegularPolygon(g, 0, 0, radius, 4, -Math.PI / 2);
        break;
      case 'purple':
        drawRegularPolygon(g, 0, 0, radius, 5, -Math.PI / 2);
        break;
    }
  }

  private repositionAllTiles() {
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const tile = this.grid[r][c];
        if (!tile) continue;
        const sprite = this.sprites.get(tile.id);
        if (!sprite) continue;
        sprite.setPosition(this.cellX(c), this.cellY(r));
        const g = sprite.first as Phaser.GameObjects.Graphics | null;
        if (g) this.paintTile(g, tile, this.tileSize);
      }
    }
  }

  private cellFromPointer(x: number, y: number): Cell | null {
    const col = Math.floor((x - this.boardOriginX) / this.tileSize);
    const row = Math.floor((y - this.boardOriginY) / this.tileSize);
    if (!inBounds(row, col)) return null;
    return { row, col };
  }

  private onPointerDown(pointer: Phaser.Input.Pointer) {
    if (this.busy || this.modal || this.paused) return;
    const cell = this.cellFromPointer(pointer.x, pointer.y);
    if (!cell) return;
    this.markInput();
    this.flashTileFocus(cell);
    this.drag = {
      pointerId: pointer.id,
      startX: pointer.x,
      startY: pointer.y,
      fromCell: cell,
      resolved: false,
    };
  }

  private flashTileFocus(cell: Cell) {
    const glow = this.add.graphics();
    const size = this.tileSize;
    const r = Math.max(10, size * 0.1);
    glow.lineStyle(5, 0xffd24a, 1);
    glow.strokeRoundedRect(-size / 2 + 3, -size / 2 + 3, size - 6, size - 6, r);
    glow.setPosition(this.cellX(cell.col), this.cellY(cell.row));
    this.boardContainer.add(glow);
    this.tweens.add({
      targets: glow,
      alpha: 0,
      scale: 1.1,
      duration: 260,
      ease: 'Cubic.easeOut',
      onComplete: () => glow.destroy(),
    });
  }

  private onPointerMove(pointer: Phaser.Input.Pointer) {
    if (!this.drag || this.drag.resolved || this.drag.pointerId !== pointer.id) return;
    if (this.busy) return;
    const dx = pointer.x - this.drag.startX;
    const dy = pointer.y - this.drag.startY;
    const threshold = this.tileSize * DRAG_THRESHOLD_FRAC;
    if (Math.abs(dx) < threshold && Math.abs(dy) < threshold) return;
    let dRow = 0;
    let dCol = 0;
    if (Math.abs(dx) > Math.abs(dy)) dCol = dx > 0 ? 1 : -1;
    else dRow = dy > 0 ? 1 : -1;
    const from = this.drag.fromCell;
    const to: Cell = { row: from.row + dRow, col: from.col + dCol };
    this.drag.resolved = true;
    if (inBounds(to.row, to.col)) {
      void this.attemptSwap(from, to);
    }
  }

  private onPointerUp(pointer: Phaser.Input.Pointer) {
    if (this.drag && this.drag.pointerId === pointer.id) {
      this.drag = null;
    }
  }

  private async attemptSwap(a: Cell, b: Cell) {
    if (!areAdjacent(a, b)) return;
    const ta = this.grid[a.row][a.col];
    const tb = this.grid[b.row][b.col];
    if (!ta || !tb) return;

    this.busy = true;
    swapCells(this.grid, a, b);
    await Promise.all([
      this.moveTileSprite(ta, b.row, b.col, DUR_SWAP),
      this.moveTileSprite(tb, a.row, a.col, DUR_SWAP),
    ]);

    const matches = findMatches(this.grid);
    if (matches.length === 0) {
      playSFX('swap-fail');
      swapCells(this.grid, a, b);
      await Promise.all([this.shakeTile(ta), this.shakeTile(tb)]);
      await Promise.all([
        this.moveTileSprite(ta, a.row, a.col, DUR_SWAP),
        this.moveTileSprite(tb, b.row, b.col, DUR_SWAP),
      ]);
      this.busy = false;
      return;
    }

    this.game.events.emit('tutorial:swap-success');
    playSFX('swap');
    await this.resolveCascades();
    this.busy = false;
    this.checkEnd();
  }

  private async resolveCascades() {
    let combo = 0;
    let previousHeroKinds = new Set<TileKind>();
    while (true) {
      const matches = findMatches(this.grid);
      if (matches.length === 0) break;
      combo += 1;
      if (combo >= 2) this.game.events.emit('tutorial:cascade');
      this.comboText.setText(`Combo: ${combo}`);

      const matchCountsByKind = new Map<TileKind, number>();
      const toClear = new Set<TileId>();
      const bombPromotions: { cell: Cell }[] = [];
      for (const m of matches) {
        for (const cell of m.cells) {
          const tile = this.grid[cell.row][cell.col];
          if (tile && !toClear.has(tile.id)) {
            toClear.add(tile.id);
            matchCountsByKind.set(tile.kind, (matchCountsByKind.get(tile.kind) ?? 0) + 1);
          }
        }
        if (m.cells.length >= 4) {
          const midIdx = Math.floor(m.cells.length / 2);
          bombPromotions.push({ cell: m.cells[midIdx] });
        }
      }

      const bombCells: Cell[] = [];
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          const t = this.grid[r][c];
          if (t && t.bomb && toClear.has(t.id)) bombCells.push({ row: r, col: c });
        }
      }
      for (const bc of bombCells) {
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            const nr = bc.row + dr;
            const nc = bc.col + dc;
            if (nr < 0 || nr >= ROWS || nc < 0 || nc >= COLS) continue;
            const t = this.grid[nr][nc];
            if (t) toClear.add(t.id);
          }
        }
      }

      for (const promo of bombPromotions) {
        const tile = this.grid[promo.cell.row][promo.cell.col];
        if (!tile || tile.bomb) continue;
        tile.bomb = true;
        toClear.delete(tile.id);
        const sprite = this.sprites.get(tile.id);
        if (sprite) {
          const gg = sprite.first as Phaser.GameObjects.Graphics | null;
          if (gg) this.paintTile(gg, tile, this.tileSize);
        }
      }

      if (bombCells.length > 0) {
        this.cameras.main.shake(220, 0.012);
        this.cameras.main.flash(140, 255, 120, 40, false);
      }

      let portalActivated = false;
      const allPortalCells: Cell[] = [];
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          if (this.grid[r][c]?.portal) allPortalCells.push({ row: r, col: c });
        }
      }
      const matchedPortal = allPortalCells.some((cell) => {
        const t = this.grid[cell.row][cell.col];
        return t ? toClear.has(t.id) : false;
      });
      if (matchedPortal) {
        portalActivated = true;
        for (const p of allPortalCells) {
          for (const [dr, dc] of [
            [0, 0],
            [-1, 0],
            [1, 0],
            [0, -1],
            [0, 1],
          ]) {
            const nr = p.row + dr;
            const nc = p.col + dc;
            if (nr < 0 || nr >= ROWS || nc < 0 || nc >= COLS) continue;
            const t = this.grid[nr][nc];
            if (t) toClear.add(t.id);
          }
        }
        this.drawPortalActivation(allPortalCells);
        this.cameras.main.flash(180, 168, 85, 247, false);
        this.cameras.main.shake(240, 0.014);
      }
      if (portalActivated) playSFX('power-fire');

      const allGravityCells: Cell[] = [];
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          if (this.grid[r][c]?.gravity) allGravityCells.push({ row: r, col: c });
        }
      }
      const matchedGravity = allGravityCells.filter((cell) => {
        const t = this.grid[cell.row][cell.col];
        return t ? toClear.has(t.id) : false;
      });
      if (matchedGravity.length > 0) {
        for (const gc of matchedGravity) {
          for (let dr = -2; dr <= 2; dr++) {
            for (let dc = -2; dc <= 2; dc++) {
              if (Math.abs(dr) + Math.abs(dc) > 2) continue;
              const nr = gc.row + dr;
              const nc = gc.col + dc;
              if (nr < 0 || nr >= ROWS || nc < 0 || nc >= COLS) continue;
              const t = this.grid[nr][nc];
              if (t) toClear.add(t.id);
            }
          }
        }
        this.drawGravityActivation(matchedGravity);
        this.cameras.main.flash(220, 99, 102, 241, false);
        this.cameras.main.shake(280, 0.018);
        playSFX('power-fire');
      }

      const activeHeroKinds = this.heroKinds.filter(
        (k) => (matchCountsByKind.get(k) ?? 0) > 0,
      );
      const sameStepTeamUp = activeHeroKinds.length >= 2;
      const crossStepTeamUp =
        activeHeroKinds.length > 0 &&
        previousHeroKinds.size > 0 &&
        activeHeroKinds.some((k) => !previousHeroKinds.has(k));
      const teamUp = sameStepTeamUp || crossStepTeamUp;
      const stepMultiplier = teamUp ? this.teamUpMultiplier : 1;
      if (teamUp) this.showTeamUpBanner();
      for (const heroKind of activeHeroKinds) {
        const count = matchCountsByKind.get(heroKind) ?? 0;
        this.onHeroCascadeStep(heroKind, count, combo, stepMultiplier);
      }
      previousHeroKinds = new Set(activeHeroKinds);

      const comboBonus = Math.max(0, combo - 1);
      for (const heroId of this.team) {
        const hero = HEROES[heroId];
        const count = matchCountsByKind.get(hero.tileKind) ?? 0;
        if (count > 0) {
          this.battle = addPowerCharge(this.battle, heroId, count + comboBonus);
        }
      }
      if (combo >= 2) {
        for (const heroId of this.team) {
          this.battle = addPowerCharge(this.battle, heroId, 1);
        }
      }
      this.positionAndRedrawBattleUi();

      await this.popTiles(toClear);

      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          const t = this.grid[r][c];
          if (t && toClear.has(t.id)) this.grid[r][c] = null;
        }
      }

      const falls = applyGravity(this.grid);
      const spawns = refill(this.grid, this.allKinds);

      const fallPromises = falls.map((f) =>
        this.moveTileSprite(
          f.tile,
          f.toRow,
          f.col,
          Math.max(DUR_FALL_MIN, DUR_FALL_PER_ROW * (f.toRow - f.fromRow)),
        ),
      );

      const spawnsByCol = new Map<number, typeof spawns>();
      for (const s of spawns) {
        const list = spawnsByCol.get(s.col) ?? [];
        list.push(s);
        spawnsByCol.set(s.col, list);
      }
      const spawnPromises: Promise<void>[] = [];
      for (const [, list] of spawnsByCol) {
        const stack = list.length;
        list.forEach((s) => {
          spawnPromises.push(this.spawnTile(s.tile, s.row, s.col, s.row - stack));
        });
      }

      await Promise.all([...fallPromises, ...spawnPromises]);
    }

    if (this.level.portalTiles) {
      this.ensurePortalCount(2);
    }
    if (this.level.gravityTiles) {
      this.ensureGravityCount(1);
    }

    if (combo > 0) {
      this.time.delayedCall(800, () => {
        if (!this.busy) this.comboText.setText('');
      });
    }
  }

  private ensureGravityCount(target: number) {
    let existing: Cell[] = [];
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        if (this.grid[r][c]?.gravity) existing.push({ row: r, col: c });
      }
    }
    const needed = target - existing.length;
    if (needed <= 0) return;

    const candidates: Cell[] = [];
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const t = this.grid[r][c];
        if (!t || t.bomb || t.portal || t.gravity) continue;
        candidates.push({ row: r, col: c });
      }
    }
    for (let i = candidates.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
    }

    let placed = 0;
    for (const c of candidates) {
      if (placed >= needed) break;
      const tile = this.grid[c.row][c.col];
      if (!tile) continue;
      tile.gravity = true;
      const sprite = this.sprites.get(tile.id);
      if (sprite) {
        const gg = sprite.first as Phaser.GameObjects.Graphics | null;
        if (gg) this.paintTile(gg, tile, this.tileSize);
      }
      placed += 1;
    }
  }

  private ensurePortalCount(target: number) {
    let existing: Cell[] = [];
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        if (this.grid[r][c]?.portal) existing.push({ row: r, col: c });
      }
    }
    const needed = target - existing.length;
    if (needed <= 0) return;

    const candidates: Cell[] = [];
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const t = this.grid[r][c];
        if (!t || t.bomb || t.portal) continue;
        candidates.push({ row: r, col: c });
      }
    }
    for (let i = candidates.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
    }

    let placed = 0;
    for (const c of candidates) {
      if (placed >= needed) break;
      const tile = this.grid[c.row][c.col];
      if (!tile) continue;
      tile.portal = true;
      const sprite = this.sprites.get(tile.id);
      if (sprite) {
        const gg = sprite.first as Phaser.GameObjects.Graphics | null;
        if (gg) this.paintTile(gg, tile, this.tileSize);
      }
      placed += 1;
    }
  }

  private drawPortalActivation(portals: Cell[]) {
    if (portals.length < 2) return;
    const a = portals[0];
    const b = portals[1];
    const ax = this.cellX(a.col);
    const ay = this.cellY(a.row);
    const bx = this.cellX(b.col);
    const by = this.cellY(b.row);
    const beam = this.add.graphics();
    beam.setDepth(50);
    beam.lineStyle(10, 0xa855f7, 0.95);
    beam.beginPath();
    beam.moveTo(ax, ay);
    beam.lineTo(bx, by);
    beam.strokePath();
    beam.lineStyle(4, 0xffffff, 0.9);
    beam.beginPath();
    beam.moveTo(ax, ay);
    beam.lineTo(bx, by);
    beam.strokePath();
    beam.setAlpha(1);
    this.tweens.add({
      targets: beam,
      alpha: 0,
      duration: 520,
      ease: 'Cubic.easeOut',
      onComplete: () => beam.destroy(),
    });
    for (const p of [a, b]) {
      const pulse = this.add.graphics();
      pulse.fillStyle(0xa855f7, 0.6);
      pulse.fillCircle(this.cellX(p.col), this.cellY(p.row), 20);
      pulse.setDepth(51);
      this.tweens.add({
        targets: pulse,
        scale: 3,
        alpha: 0,
        duration: 500,
        ease: 'Cubic.easeOut',
        onComplete: () => pulse.destroy(),
      });
    }
  }

  private drawGravityActivation(wells: Cell[]) {
    for (const w of wells) {
      const cx = this.cellX(w.col);
      const cy = this.cellY(w.row);
      const swirl = this.add.graphics();
      swirl.setDepth(52);
      swirl.lineStyle(8, 0x6366f1, 0.9);
      swirl.strokeCircle(cx, cy, 30);
      swirl.lineStyle(4, 0xffffff, 0.9);
      swirl.strokeCircle(cx, cy, 50);
      this.tweens.add({
        targets: swirl,
        scale: 2.4,
        alpha: 0,
        duration: 520,
        ease: 'Cubic.easeOut',
        onComplete: () => swirl.destroy(),
      });
      const core = this.add.graphics();
      core.fillStyle(0x0a0f1f, 1);
      core.fillCircle(cx, cy, 16);
      core.fillStyle(0x6366f1, 1);
      core.fillCircle(cx, cy, 8);
      core.setDepth(53);
      this.tweens.add({
        targets: core,
        scale: 0.2,
        alpha: 0,
        duration: 520,
        ease: 'Cubic.easeIn',
        onComplete: () => core.destroy(),
      });
    }
  }

  private moveTileSprite(tile: Tile, toRow: number, toCol: number, duration: number): Promise<void> {
    const sprite = this.sprites.get(tile.id);
    if (!sprite) return Promise.resolve();
    return new Promise((resolve) => {
      this.tweens.add({
        targets: sprite,
        x: this.cellX(toCol),
        y: this.cellY(toRow),
        duration,
        ease: 'Cubic.easeOut',
        onComplete: () => resolve(),
      });
    });
  }

  private shakeTile(tile: Tile): Promise<void> {
    const sprite = this.sprites.get(tile.id);
    if (!sprite) return Promise.resolve();
    const originX = sprite.x;
    return new Promise((resolve) => {
      this.tweens.add({
        targets: sprite,
        x: originX + 6,
        duration: DUR_SHAKE,
        yoyo: true,
        repeat: 1,
        ease: 'Sine.easeInOut',
        onComplete: () => {
          sprite.x = originX;
          resolve();
        },
      });
    });
  }

  private popTiles(ids: Set<TileId>): Promise<void> {
    if (ids.size > 0) playSFX('pop');
    const promises: Promise<void>[] = [];
    for (const id of ids) {
      const sprite = this.sprites.get(id);
      if (!sprite) continue;
      promises.push(
        new Promise<void>((resolve) => {
          this.tweens.add({
            targets: sprite,
            scale: 0,
            alpha: 0,
            duration: DUR_POP,
            ease: 'Back.easeIn',
            onComplete: () => {
              sprite.destroy();
              this.sprites.delete(id);
              resolve();
            },
          });
        }),
      );
    }
    return Promise.all(promises).then(() => undefined);
  }

  private spawnTile(tile: Tile, row: number, col: number, startRow: number): Promise<void> {
    const sprite = this.createSpriteFor(tile, startRow, col);
    sprite.setAlpha(0.2);
    return new Promise((resolve) => {
      this.tweens.add({
        targets: sprite,
        y: this.cellY(row),
        alpha: 1,
        duration: Math.max(DUR_FALL_MIN, DUR_FALL_PER_ROW * (row - startRow)),
        ease: 'Cubic.easeOut',
        onComplete: () => resolve(),
      });
    });
  }

  private showVictoryModal(
    stars: 0 | 1 | 2 | 3,
    vibraniumGained = 0,
    tiersUnlocked: string[] = [],
  ) {
    this.openModal('win', stars, vibraniumGained, tiersUnlocked);
  }

  private showRetryModal() {
    this.openModal('lose', 0, 0, []);
  }

  private openModal(
    kind: 'win' | 'lose',
    stars: 0 | 1 | 2 | 3,
    vibraniumGained: number,
    tiersUnlocked: string[],
  ) {
    if (this.modal) return;
    const w = this.scale.width;
    const h = this.scale.height;

    const container = this.add.container(0, 0);
    const dim = this.add.graphics();
    dim.fillStyle(0x000000, 0.55);
    dim.fillRect(0, 0, w, h);
    container.add(dim);

    const card = this.add.graphics();
    const cardW = Math.min(380, Math.floor(w * 0.72));
    const cardH = Math.min(340, Math.floor(h * 0.62));
    const cardX = Math.floor((w - cardW) / 2);
    const cardY = Math.floor((h - cardH) / 2);
    card.fillStyle(0x121a32, 1);
    card.lineStyle(2, 0x2a3558, 1);
    card.fillRoundedRect(cardX, cardY, cardW, cardH, 20);
    card.strokeRoundedRect(cardX, cardY, cardW, cardH, 20);
    container.add(card);

    const centerCx = cardX + cardW / 2;

    if (kind === 'win') {
      const starsG = this.add.graphics();
      this.drawModalStars(starsG, centerCx, cardY + cardH * 0.3, stars);
      container.add(starsG);

      if (vibraniumGained > 0) {
        this.drawVibraniumReward(
          container,
          centerCx,
          cardY + cardH * 0.52,
          vibraniumGained,
          tiersUnlocked,
        );
      }
    } else {
      const label = this.add.text(centerCx, cardY + cardH * 0.35, 'Try again', {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '28px',
        color: '#f4f4f4',
        fontStyle: 'bold',
      }).setOrigin(0.5);
      container.add(label);
    }

    const btnContainer = this.add.container(centerCx, cardY + cardH * 0.72);
    container.add(btnContainer);

    const btnG = this.add.graphics();
    const btnR = 54;
    const btnColor = kind === 'win' ? 0x22c55e : 0xfbbf24;
    btnG.fillStyle(btnColor, 1);
    btnG.fillCircle(0, 0, btnR);
    btnG.lineStyle(4, 0x0a0f1f, 1);
    btnG.strokeCircle(0, 0, btnR);
    btnG.lineStyle(7, 0x0a0f1f, 1);
    if (kind === 'win') {
      btnG.fillStyle(0x0a0f1f, 1);
      btnG.beginPath();
      btnG.moveTo(-btnR * 0.28, -btnR * 0.42);
      btnG.lineTo(btnR * 0.48, 0);
      btnG.lineTo(-btnR * 0.28, btnR * 0.42);
      btnG.closePath();
      btnG.fillPath();
    } else {
      btnG.beginPath();
      btnG.arc(0, 0, btnR * 0.5, -Math.PI * 0.3, Math.PI * 1.1, false);
      btnG.strokePath();
      const arrowLen = btnR * 0.28;
      const arrowTipX = btnR * 0.5 * Math.cos(-Math.PI * 0.3);
      const arrowTipY = btnR * 0.5 * Math.sin(-Math.PI * 0.3);
      btnG.beginPath();
      btnG.moveTo(arrowTipX, arrowTipY);
      btnG.lineTo(arrowTipX - arrowLen, arrowTipY - arrowLen * 0.4);
      btnG.moveTo(arrowTipX, arrowTipY);
      btnG.lineTo(arrowTipX - arrowLen * 0.3, arrowTipY + arrowLen);
      btnG.strokePath();
    }
    btnContainer.add(btnG);

    const hit = this.add.zone(0, 0, btnR * 2.6, btnR * 2.6).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => {
      if (kind === 'win') this.onVictoryNext(stars);
      else this.restart();
    });
    btnContainer.add(hit);

    this.tweens.add({
      targets: btnContainer,
      scale: 1.1,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.modal = container;
  }

  private drawModalStars(g: Phaser.GameObjects.Graphics, cx: number, cy: number, count: number) {
    const total = 3;
    const size = 22;
    const spacing = size * 2.6;
    const startX = cx - ((total - 1) * spacing) / 2;
    for (let i = 0; i < total; i++) {
      const filled = i < count;
      drawStarShape(g, startX + i * spacing, cy, size, size * 0.42, 5, filled ? 0xfbbf24 : 0x2a3558);
    }
  }

  private drawVibraniumReward(
    parent: Phaser.GameObjects.Container,
    cx: number,
    cy: number,
    count: number,
    tiersUnlocked: string[],
  ) {
    const badge = this.add.container(cx, cy);
    parent.add(badge);

    const hex = this.add.graphics();
    const r = 22;
    const points: { x: number; y: number }[] = [];
    for (let i = 0; i < 6; i++) {
      const a = -Math.PI / 2 + (i * Math.PI) / 3;
      points.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
    }
    hex.fillStyle(0xa78bfa, 1);
    hex.lineStyle(3, 0x0a0f1f, 1);
    hex.beginPath();
    hex.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) hex.lineTo(points[i].x, points[i].y);
    hex.closePath();
    hex.fillPath();
    hex.strokePath();
    hex.fillStyle(0xffffff, 0.35);
    hex.fillCircle(-r * 0.3, -r * 0.3, r * 0.22);
    badge.add(hex);

    const gainText = this.add
      .text(r + 14, 0, `+${count} VIBRANIUM!`, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '22px',
        color: '#e9d5ff',
        fontStyle: 'bold',
        stroke: '#0a0f1f',
        strokeThickness: 4,
      })
      .setOrigin(0, 0.5);
    badge.add(gainText);

    badge.setScale(0.3).setAlpha(0);
    this.tweens.add({
      targets: badge,
      scale: 1,
      alpha: 1,
      duration: 380,
      delay: 180,
      ease: 'Back.easeOut',
      onComplete: () => {
        playSFX('power-ready');
        this.tweens.add({
          targets: hex,
          scale: 1.12,
          duration: 500,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      },
    });

    if (tiersUnlocked.length > 0) {
      const tierText = this.add
        .text(cx, cy + 44, `NEW TIER: ${tiersUnlocked[0]}`, {
          fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
          fontSize: '16px',
          color: '#fbbf24',
          fontStyle: 'bold',
          stroke: '#0a0f1f',
          strokeThickness: 4,
        })
        .setOrigin(0.5)
        .setAlpha(0);
      parent.add(tierText);
      this.tweens.add({
        targets: tierText,
        alpha: 1,
        duration: 400,
        delay: 600,
        ease: 'Cubic.easeOut',
      });
    }
  }

  private onVictoryNext(stars: 0 | 1 | 2 | 3) {
    this.modal?.destroy(true);
    this.modal = undefined;
    this.hideTelegraph();
    if (this.level.midUnlock) {
      const heroId = this.level.midUnlock;
      unlockHero(heroId);
      this.showMidUnlockToast(heroId, () => this.scene.start('LevelMap'));
      return;
    }
    if (this.level.isBoss) {
      const save = loadSave();
      const total = (Object.values(save.starsByLevelId) as number[]).reduce((a, b) => a + b, 0);
      const worldLevels = WORLDS[this.level.world].length;
      const maxStars = worldLevels * 3;
      this.scene.start('WorldClear', {
        worldNumber: this.level.world,
        totalStars: total,
        maxStars,
        starsThisRun: stars,
      });
    } else {
      this.scene.start('LevelMap');
    }
  }

  private showMidUnlockToast(heroId: HeroId, onDone: () => void) {
    const w = this.scale.width;
    const h = this.scale.height;
    const container = this.add.container(0, 0);

    const dim = this.add.graphics();
    dim.fillStyle(0x000000, 0.7);
    dim.fillRect(0, 0, w, h);
    container.add(dim);

    const displayName = HEROES[heroId].displayName;

    const sparkle = this.add.graphics();
    sparkle.lineStyle(3, 0xffd24a, 0.7);
    const cx = w / 2;
    const cy = h * 0.48;
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      sparkle.beginPath();
      sparkle.moveTo(cx + Math.cos(a) * 70, cy + Math.sin(a) * 70);
      sparkle.lineTo(cx + Math.cos(a) * 110, cy + Math.sin(a) * 110);
      sparkle.strokePath();
    }
    this.tweens.add({
      targets: sparkle,
      alpha: 0.3,
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    container.add(sparkle);

    const title = this.add
      .text(cx, h * 0.36, 'NEW HERO!', {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '32px',
        color: '#fbbf24',
        fontStyle: 'bold',
        stroke: '#0a0f1f',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setScale(0.4)
      .setAlpha(0);
    container.add(title);

    const nameText = this.add
      .text(cx, h * 0.56, `${displayName} joined!`, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '44px',
        color: '#ffffff',
        fontStyle: 'bold',
        stroke: '#0a0f1f',
        strokeThickness: 8,
      })
      .setOrigin(0.5)
      .setScale(0.4)
      .setAlpha(0);
    container.add(nameText);

    this.tweens.add({
      targets: [title, nameText],
      scale: 1,
      alpha: 1,
      duration: 300,
      ease: 'Back.easeOut',
    });

    this.time.delayedCall(2200, () => {
      container.destroy(true);
      onDone();
    });
  }

  private restart() {
    this.modal?.destroy(true);
    this.modal = undefined;
    this.hideTelegraph();
    this.scene.restart({ levelId: this.level.id, team: this.team });
  }

  private openPauseMenu() {
    if (this.paused || this.modal) return;
    this.paused = true;
    this.hideHint();
    this.hideTelegraph();

    const w = this.scale.width;
    const h = this.scale.height;
    const container = this.add.container(0, 0);

    const dim = this.add.graphics();
    dim.fillStyle(0x000000, 0.55);
    dim.fillRect(0, 0, w, h);
    container.add(dim);
    const dimZone = this.add.zone(0, 0, w, h).setOrigin(0).setInteractive();
    container.add(dimZone);

    const cardW = Math.min(420, Math.floor(w * 0.78));
    const cardH = 210;
    const cardX = Math.floor((w - cardW) / 2);
    const cardY = Math.floor((h - cardH) / 2);
    const card = this.add.graphics();
    card.fillStyle(0x121a32, 1);
    card.lineStyle(2, 0x2a3558, 1);
    card.fillRoundedRect(cardX, cardY, cardW, cardH, 20);
    card.strokeRoundedRect(cardX, cardY, cardW, cardH, 20);
    container.add(card);

    const title = this.add.text(w / 2, cardY + 32, 'Paused', {
      fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
      fontSize: '26px',
      color: '#ffd24a',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(title);

    const buttonY = cardY + cardH - 70;
    const gap = cardW * 0.2;
    const cx = cardX + cardW / 2;
    const save = loadSave();

    this.addPauseButton(container, cx - gap * 1.5, buttonY, 'resume', () => this.closePauseMenu());
    this.addPauseButton(container, cx - gap * 0.5, buttonY, 'retry', () => {
      this.closePauseMenu();
      this.scene.restart({ levelId: this.level.id, team: this.team });
    });
    this.addPauseButton(container, cx + gap * 0.5, buttonY, 'home', () => {
      this.closePauseMenu();
      this.scene.start('LevelMap');
    });
    this.addPauseButton(
      container,
      cx + gap * 1.5,
      buttonY,
      save.audioMuted ? 'muted' : 'unmuted',
      () => {
        const newState = !loadSave().audioMuted;
        setMuted(newState);
        applyMuteFromSave();
        this.closePauseMenu();
        this.openPauseMenu();
      },
    );

    this.pauseModal = container;
  }

  private addPauseButton(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    kind: 'resume' | 'retry' | 'home' | 'muted' | 'unmuted',
    onTap: () => void,
  ) {
    const r = 32;
    const g = this.add.graphics();
    const fill = kind === 'resume' ? 0x22c55e : 0xffd24a;
    g.fillStyle(fill, 1);
    g.lineStyle(3, 0x0a0f1f, 1);
    g.fillCircle(x, y, r);
    g.strokeCircle(x, y, r);

    g.lineStyle(4, 0x0a0f1f, 1);
    g.fillStyle(0x0a0f1f, 1);
    switch (kind) {
      case 'resume':
        g.beginPath();
        g.moveTo(x - 8, y - 12);
        g.lineTo(x - 8, y + 12);
        g.lineTo(x + 12, y);
        g.closePath();
        g.fillPath();
        break;
      case 'retry':
        g.lineStyle(5, 0x0a0f1f, 1);
        g.beginPath();
        g.arc(x, y, r * 0.5, -Math.PI * 0.3, Math.PI * 1.1, false);
        g.strokePath();
        {
          const arrowLen = r * 0.28;
          const tipX = x + r * 0.5 * Math.cos(-Math.PI * 0.3);
          const tipY = y + r * 0.5 * Math.sin(-Math.PI * 0.3);
          g.beginPath();
          g.moveTo(tipX, tipY);
          g.lineTo(tipX - arrowLen, tipY - arrowLen * 0.4);
          g.moveTo(tipX, tipY);
          g.lineTo(tipX - arrowLen * 0.3, tipY + arrowLen);
          g.strokePath();
        }
        break;
      case 'home':
        g.lineStyle(4, 0x0a0f1f, 1);
        g.beginPath();
        g.moveTo(x - 14, y + 2);
        g.lineTo(x, y - 14);
        g.lineTo(x + 14, y + 2);
        g.strokePath();
        g.fillStyle(0x0a0f1f, 1);
        g.fillRect(x - 10, y, 20, 12);
        break;
      case 'muted':
      case 'unmuted': {
        g.fillStyle(0x0a0f1f, 1);
        g.beginPath();
        g.moveTo(x - 10, y - 6);
        g.lineTo(x - 4, y - 6);
        g.lineTo(x + 4, y - 12);
        g.lineTo(x + 4, y + 12);
        g.lineTo(x - 4, y + 6);
        g.lineTo(x - 10, y + 6);
        g.closePath();
        g.fillPath();
        if (kind === 'muted') {
          g.lineStyle(3, 0xef4444, 1);
          g.beginPath();
          g.moveTo(x + 8, y - 10);
          g.lineTo(x + 16, y + 10);
          g.moveTo(x + 16, y - 10);
          g.lineTo(x + 8, y + 10);
          g.strokePath();
        } else {
          g.lineStyle(3, 0x0a0f1f, 1);
          g.beginPath();
          g.arc(x + 10, y, 6, -Math.PI / 3, Math.PI / 3, false);
          g.strokePath();
        }
        break;
      }
    }
    container.add(g);

    const hit = this.add.zone(x, y, r * 2.4, r * 2.4).setInteractive({ useHandCursor: true });
    hit.on('pointerup', onTap);
    container.add(hit);
  }

  private closePauseMenu() {
    this.pauseModal?.destroy(true);
    this.pauseModal = undefined;
    this.paused = false;
  }
}

function drawStarShape(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  points: number,
  fill: number,
) {
  const pts: { x: number; y: number }[] = [];
  const step = Math.PI / points;
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const a = -Math.PI / 2 + i * step;
    pts.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
  }
  g.fillStyle(fill, 1);
  g.lineStyle(1, 0x0a0f1f, 1);
  g.beginPath();
  g.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
  g.closePath();
  g.fillPath();
  g.strokePath();
}

function drawRegularPolygon(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  radius: number,
  sides: number,
  rotation: number,
) {
  const pts: Phaser.Math.Vector2[] = [];
  for (let i = 0; i < sides; i++) {
    const a = rotation + (i * Math.PI * 2) / sides;
    pts.push(new Phaser.Math.Vector2(cx + radius * Math.cos(a), cy + radius * Math.sin(a)));
  }
  g.beginPath();
  g.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
  g.closePath();
  g.fillPath();
  g.strokePath();
}
